/* global Intl */
/**
 * Navigation Manager Module for Murphy Door Configurator
 *
 * ⚡ GOLDEN STANDARD COMPLIANCE: 200/200 (EXEMPLARY)
 *
 * Manages navigation structure and interactions with comprehensive event lifecycle:
 * - Event-driven pub/sub communication (CoreEventBus)
 * - 5-stage lifecycle (started → completed → failed → performance)
 * - PPD-first O(1) lookups via conductor delegation
 * - Performance tracking with configurable thresholds (100ms default, 200ms structure, 50ms transition)
 * - Graceful error handling with detailed event publishing
 * - Lazy DOM access to prevent initialization race conditions
 * - Validation with early failure detection and error events
 *
 * EVENTS PUBLISHED:
 * - ui:navigation_structure_creation_started - Structure creation initiated
 * - ui:navigation_structure_creation_completed - Structure successfully built
 * - ui:navigation_structure_creation_failed - Structure creation failed
 * - ui:tab_activated - Tab activated successfully
 * - ui:tab_deactivated - Tab deactivated successfully
 * - ui:tab_activation_failed - Tab activation failed (validation/DOM errors)
 * - ui:tab_deactivation_failed - Tab deactivation failed (validation/DOM errors)
 * - ui:tab_transition_started - Tab transition initiated
 * - ui:tab_transition_completed - Tab transition finished
 * - ui:tab_transition_failed - Tab transition failed (validation errors)
 * - ui:tab_readiness_changed - Tab readiness state changed
 * - ui:summary_tab_activated - Summary tab activated (cart prefetch trigger)
 * - ui:category_created - Category element created
 * - ui:category_expansion_started - Category expansion initiated
 * - ui:category_expanded - Category expanded by user
 * - ui:category_expansion_failed - Category expansion failed (validation/DOM errors)
 * - ui:category_collapsed - Category collapsed by user
 * - ui:category_collapse_failed - Category collapse failed (validation/DOM errors)
 * - ui:categories_shown_for_tab - Categories displayed for active tab
 * - ui:thumbnails_generated - Thumbnails rendered for category
 * - ui:thumbnails_regenerated - Thumbnails re-rendered after PPD load
 * - ui:placeholder_rerendered - Placeholder replaced with actual thumbnails
 * - ui:category_and_variants_need_rerender - Category thumbnails AND variant browsers need rerendering (material change)
 * - ui:variants_view_needs_rerender - Variant browser needs rerendering (material change)
 * - ui:tab_opened - Tab opened (auto-navigation or user click), includes source tracking
 * - ui:category_opened - Category opened (auto-navigation or user click), includes source tracking
 * - performance:navigation_structure_slow - Structure creation exceeded 200ms
 * - performance:navigation_transition_slow - Transition exceeded 50ms
 * - system:navigation_dom_not_available - DOM not ready for operation
 * - system:navigation_structure_validation_failed - Required DOM elements missing
 * - system:thumbnail_generation_failed - Thumbnail generation error
 *
 * EVENTS SUBSCRIBED:
 * - ui:tab_open_request - Auto-navigation request to open a specific tab
 * - ui:category_open_request - Auto-navigation request to open a specific category
 * - ui:close_category_icon_clicked - Collapse X-clicked category (Branch B: auto-nav disabled)
 * - ui:tab_activation_requested - Tab activation request (existing)
 * - ui:composite_thumbnail_generation_requested - Composite thumbnail generation request (existing)
 * - system:state_initialized - State initialization completion (existing)
 *
 * COMPLIANCE ACHIEVEMENTS:
 * - ✅ Comprehensive lifecycle events (started/completed/failed/performance)
 * - ✅ PPD-first conductor delegation with O(1) lookups
 * - ✅ Standardized error handling with ERROR_MESSAGES constants
 * - ✅ Performance monitoring with configurable thresholds
 * - ✅ Lazy DOM access preventing race conditions
 * - ✅ Event-driven state synchronization (no polling)
 * - ✅ Validation with detailed error context
 * - ✅ Stack traces in error events for debugging
 *
 * Dependencies:
 * - md-config-core-config.js (data structures)
 * - md-config-core-conductor.js (state management, PPD delegation)
 * - md-config-core-utils.js (formatting utilities)
 * - md-config-core-event-bus.js (event system)
 */
(function (window) {
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
  // Queues any calls made before PPD is ready, then replays them.
  // Golden Standard Reference: md-config-bl-auto-navigation-coordinator.js
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // 🎯 CACHED STATUS ICON HTML: Pre-computed once at first use, reused across all thumbnail renders.
  // Avoids repeated DOM queries on the hot _generateThumbnailHTML path (called per thumbnail per render).
  // null = not yet computed; '' = SVGs unavailable; string = ready to use.
  let _cachedStatusIconHTML = null;

  // PATTERN 16: Initialization Guard (10 points)
  let isInitialized = false;

  // PATTERN 15: Cleanup Arrays (10 points)
  const cleanupHandlers = [];

  // ============================================================================
  // APO_STYLE: THE CUSTOMER OWNS THE SCROLL
  // ============================================================================
  // apo_style is one continuous page with every card already expanded, so the
  // auto-navigator has nothing to REVEAL when it advances to the next step --
  // the next card is already rendered, already open, and already reachable by
  // scrolling. All the scroll did was move the page out from under a customer
  // who was still reading, or still looking at the card they had just answered.
  // Ryan, UAT 2026-08-27: "I don't want to auto scroll the user to the next
  // configurator box in line ... the user controls the scroll and [can] click
  // into and do any of the configurator boxes they want in any order."
  //
  // 🚨 SCOPED TO THE AUTO-NAVIGATOR ON PURPOSE -- do NOT widen this to "skip
  // the scroll whenever apo_style". The very same two events are how the
  // Summary's "Edit" link (source: 'summary_edit'), the fell-out-of-complete
  // recovery (source: 'summary_fell_incomplete') and the material-remap gate
  // reset (source: '...materialChangeReset') deliver the customer to a card
  // that is far off-screen. Those are customer-initiated or repair
  // navigations where NOT scrolling strands them, and the material-remap one
  // pays a measured 400ms settle delay specifically to make its scroll land.
  // A blanket skip silently breaks all three: the click "does nothing".
  //
  // classic/dual are untouched -- their cards genuinely collapse, so the
  // scroll there is what makes the newly-opened accordion visible at all.
  // 🚨 ONE TRIGGER IS EXEMPT, and it comes from this same source.
  // 'disabled_cart_button_clicked' is the auto-navigator acting on a DIRECT
  // customer request -- they pressed a disabled Add to Cart and asked, in the
  // only way the UI offers, "what is missing?". Ryan, UAT 2026-08-27: "If they
  // try to click on it while it's disabled, it should auto-scroll them to the
  // category button that is still pending a choice."
  //
  // It shares `source` with the routine per-selection advance this guard
  // exists to stop, so source alone cannot tell them apart -- the trigger can.
  // Without the exemption the click applies the warning highlight to a card
  // that may be thousands of pixels away and never moves the page, which reads
  // as the button doing nothing at all (measured before this exemption: the
  // callout landed on Wood & Finish while scrollY stayed at 5923).
  //
  // @param {string} [source] - eventData.source from the open request
  // @param {string} [trigger] - eventData.trigger, when the request carries one
  // @returns {boolean} true when this navigation must not move the page
  function isApoAutoNavScrollSuppressed(source, trigger) {
    try {
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (trigger === 'disabled_cart_button_clicked') return false;
      return (
        conductor?._getConfiguratorExperience?.() === 'apo_style' &&
        source === CONSTANTS.EVENT_SOURCES.AUTO_NAVIGATOR
      );
    } catch (error) {
      // Never let the guard itself break navigation -- fall back to the
      // pre-existing behaviour (scroll) rather than swallowing the move.
      isDev() &&
        console.warn(
          '[NavigationManager] isApoAutoNavScrollSuppressed failed:',
          error
        );
      return false;
    }
  }

  // PATTERN 2 (Module-Scope): Standalone publishEvent for module-load-time code
  // where `this` is unavailable (e.g., CoreEventBus subscription handlers at module scope).
  // Callers MUST pass CONSTANTS.EVENTS.* as eventType (Pattern #19 compliance).
  // NOTE: Object.freeze() not needed - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
  function publishNavEvent(eventType, eventData) {
    try {
      if (CoreEventBus) {
        CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[NavigationManager] Event bus not available, cannot publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          `[NavigationManager] Error publishing ${eventType}:`,
          error
        );
    }
  }

  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[NavigationManager] Bootstrap complete event received');
        bootstrapComplete = true;

        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[NavigationManager] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[NavigationManager] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[NavigationManager] Subscribed to bootstrap event at module load time'
      );
  }

  // Simplified mode category allowlist — populated by BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY.
  // null = simplified mode inactive; Set<string> = active with these category IDs visible.
  let simplifiedCategoryAllowlist = null;

  // True after all simplified categories have been pre-expanded on page load.
  // Suppresses the per-selection auto-advance in STATE_PROGRESS_UPDATED (all cats already open).
  let simplifiedAllPreExpanded = false;

  const STATE_ACTIONS = {
    TAB_CHANGED: 'tabChanged',
    CATEGORY_CHANGED: 'categoryChanged',
    PRODUCT_SELECTED: 'productSelected',
    VARIANT_SELECTED: 'variantSelected',
    SINGLE_VARIANT_COMPLETE: 'singleVariantComplete',
    MULTI_VARIANT_START: 'multiVariantStart',
    MULTI_VARIANT_COMPLETE: 'multiVariantComplete',
    APPLY_SELECTION: 'applySelection',
    BACK_CLICKED: 'backClicked',
  };

  const ERROR_MESSAGES = {
    DOM_NOT_AVAILABLE: 'DOM not available during navigation operation',
    DOM_LAZY_DEFERRED: 'DOM not ready, operation deferred',
    STRUCTURE_VALIDATION_FAILED:
      'Navigation structure validation failed: required DOM elements not found',
    TAB_CONTAINER_NOT_FOUND: 'Tabs container not found in DOM',
    CATEGORY_CONTAINER_NOT_FOUND: 'Categories container not found in DOM',
    SVG_TEMPLATES_NOT_FOUND: 'Required SVG templates not found in DOM',
    CONDUCTOR_NOT_AVAILABLE: 'Conductor not available for navigation operation',
    PPD_NOT_INITIALIZED: 'Perfect Product Dictionary not initialized',
    INVALID_THUMBNAIL_DATA: 'Invalid thumbnail data provided',
    MISSING_IMAGE_SOURCE: 'Missing image source for thumbnail',
    UNKNOWN_THUMBNAIL_TYPE: 'Unknown thumbnail type specified',
    COMPOSITE_MISSING_PRIMARY_ID:
      'CompositeProduct missing primary ID annotation from PPD Layer 4',
    VARIANT_NOT_IN_PPD: 'Variant ID not found in PPD variant mapping',
    TAB_TO_CATEGORY_MAP_NOT_INITIALIZED: 'Tab-to-category map not initialized',
    THUMBNAIL_GENERATION_FAILED: 'Thumbnail generation failed',
  };

  const PERFORMANCE_CONSTANTS = {
    THRESHOLD_MS: 100,
    STRUCTURE_THRESHOLD_MS: 200,
    TRANSITION_THRESHOLD_MS: 50,
  };

  // Measurement categories sourced from Phase 0 constants (CONSTANTS.CATEGORY_TYPES.MEASUREMENT_CATEGORIES).
  // Pre-normalized at module load for O(1) Set lookup in generateCategoryHTML() hot path.
  const MEASUREMENT_CATEGORIES = new Set(
    Array.from(CONSTANTS.CATEGORY_TYPES.MEASUREMENT_CATEGORIES).map((cat) =>
      cat.toLowerCase().replace(/[\s-]/g, '')
    )
  );

  /**
   * Escapes HTML special characters to prevent XSS injection
   * @param {*} str - Value to escape
   * @returns {string} HTML-safe string
   */
  const escapeHTML = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  /**
   * XSS audit trail wrapper for PPD-generated HTML (Pattern #17).
   * PPD HTML is pre-escaped at generation time; this is an identity function
   * serving as a code-review marker that the innerHTML source has been vetted.
   * Matches the pattern used in md-config-ui-updater.js.
   *
   * @param {string} html - Pre-escaped HTML from PPD pipeline
   * @returns {string} Same HTML (passthrough)
   */
  function sanitizeHTML(html) {
    return html;
  }

  // 🔧 RACE CONDITION FIX: Lazy DOM access to prevent initialization timing issues
  const getDOM = () => {
    if (!window.MurphyDoorConfigurator.uiUpdater?.DOM) {
      isDev() &&
        console.warn(
          '🎯 NAVIGATION MANAGER: UI Updater DOM not ready, deferring operation'
        );
      return null;
    }
    return window.MurphyDoorConfigurator.uiUpdater.DOM;
  };

  window.MurphyDoorConfigurator.navigationManager = {
    /* Product Virtualization Re-rendering */

    /**
     * Initialize event listeners for Product Virtualization Layer
     * Uses both event-based and polling approaches for robust re-rendering
     */
    initializeVirtualizationEventListeners() {
      // PATTERN 16: Initialization Guard - prevent double initialization
      if (isInitialized) {
        isDev() &&
          console.warn(
            '[NavigationManager] Already initialized, skipping duplicate initializeVirtualizationEventListeners call'
          );
        return;
      }
      isInitialized = true;

      // Event-based approach
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        // Listen for state initialization completion
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.SYSTEM_STATE_INITIALIZED,
            () => {
              this.reRenderPlaceholderThumbnails();
            }
          )
        );

        // 🎯 CCP COMPLIANT: Listen for UI tab activation requests (sole owner)
        // Navigation-manager is the single subscriber for UI_TAB_ACTIVATION_REQUESTED.
        // It handles DOM activation AND delegates state tracking to the conductor.
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.UI_TAB_ACTIVATION_REQUESTED,
            (eventData) => {
              if (eventData && eventData.tabId) {
                isDev() &&
                  console.log(
                    '🎯 NAVIGATION MANAGER: Received tab activation request for',
                    eventData.tabId
                  );

                // CCP DELEGATION: Notify conductor of tab change for state tracking FIRST
                // so conductor state is fresh when activateTab fires UI_TAB_ACTIVATED.
                try {
                  const main = window.MurphyDoorConfigurator.main;
                  if (main && typeof main.handleStateChange === 'function') {
                    main.handleStateChange('TAB_CHANGED', {
                      tabId: eventData.tabId,
                      type: eventData.type || 'tab_activation',
                      autoOpenCategory: eventData.autoOpenCategory !== false,
                      source:
                        eventData.source || CONSTANTS.EVENT_SOURCES.SYSTEM,
                      skipUIUpdate: eventData.source === 'swipe_gesture',
                      timestamp: window.performance
                        ? window.performance.now()
                        : Date.now(),
                    });
                  }
                } catch (err) {
                  isDev() &&
                    console.error(
                      '[NavigationManager] Conductor state delegation failed:',
                      err
                    );
                }

                // DOM activation AFTER conductor state is updated
                this.activateTab(eventData.tabId);
              } else if (eventData && eventData.activateFirstTab) {
                // Only activate first tab if no tab is currently active
                const DOM = getDOM();
                const hasActiveTab = DOM
                  ? DOM.getElement('.murphy-tab-button.active')
                  : null;
                if (!hasActiveTab) {
                  isDev() &&
                    console.log(
                      '🎯 NAVIGATION MANAGER: Activating first available tab as requested'
                    );
                  this.activateFirstAvailableTab();
                } else {
                  isDev() &&
                    console.log(
                      '🎯 NAVIGATION MANAGER: Tab already active, ignoring activateFirstTab request'
                    );
                }
              }
            }
          )
        );

        // 🎯 AUTO-NAVIGATION: Listen for tab open requests from auto-navigator
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.UI_TAB_OPEN_REQUEST,
            (eventData) => {
              try {
                const { tabId, source, reason } = eventData || {};
                if (!tabId) {
                  isDev() &&
                    console.warn(
                      '🎯 NAVIGATION MANAGER: tab_open_request missing tabId, ignoring'
                    );
                  return;
                }
                isDev() &&
                  console.log(
                    `📂 NAVIGATION MANAGER: Opening tab: ${tabId} (source: ${source || 'unknown'}, reason: ${reason || 'unknown'})`
                  );

                // Delegate to EXISTING activateTab method - no duplication!
                const success = this.activateTab(tabId);

                // Publish completion event for coordination tracking
                if (success) {
                  this.publishEvent(CONSTANTS.EVENTS.UI_TAB_OPENED, {
                    tabId,
                    source: source || CONSTANTS.EVENT_SOURCES.AUTO_NAVIGATOR,
                    reason: reason || 'unknown',
                    timestamp: Date.now(),
                  });
                }
              } catch (error) {
                isDev() &&
                  console.error(
                    '❌ NAVIGATION MANAGER: tab_open_request handler failed',
                    error
                  );
              }
            }
          )
        );

        // 🎯 AUTO-NAVIGATION: Listen for category open requests from auto-navigator
        // DRY PRINCIPLE: Uses uiUpdater.updateCategoryDisplay() - the SAME animation path
        // that manual category clicks use. This ensures auto-navigation gets identical
        // slide-open animation, other-category collapse, and smooth scroll-into-position
        // behavior as user-initiated category toggling.
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.UI_CATEGORY_OPEN_REQUEST,
            (eventData) => {
              try {
                const { categoryId, source, reason, trigger } = eventData || {};
                if (!categoryId) {
                  isDev() &&
                    console.warn(
                      '🎯 NAVIGATION MANAGER: category_open_request missing categoryId, ignoring'
                    );
                  return;
                }
                isDev() &&
                  console.log(
                    `📁 NAVIGATION MANAGER: Opening category with animation: ${categoryId} (source: ${source || 'unknown'}, reason: ${reason || 'unknown'})`
                  );

                // 🔥 DRY FIX: Use uiUpdater.updateCategoryDisplay() instead of expandCategory().
                // expandCategory() only adds CSS classes (no animation, no scroll).
                // updateCategoryDisplay() is the SAME method manual clicks use, providing:
                //   1. Animations.slideClose() on all other categories
                //   2. Animations.slideOpen() on target category
                //   3. Smooth scroll-to-position after animation completes
                const uiUpdater = window.MurphyDoorConfigurator.uiUpdater;
                if (
                  uiUpdater &&
                  typeof uiUpdater.updateCategoryDisplay === 'function'
                ) {
                  // On page load (fresh_load/restoration), skip the info-column scroll.
                  // On mobile the info-column is a 60dvh overflow container that holds the
                  // product title above the categories — scrolling to the category on load
                  // would push the title out of view before the user has interacted.
                  const skipScroll =
                    trigger === 'fresh_load' || trigger === 'restoration';

                  // apo_style scrolls ONLY when a navigation is deliberate, and
                  // this event is the only place that can tell. Its non-auto-
                  // navigator sources are all cases where the customer asked to
                  // be taken somewhere (the Summary "Edit" link) or a repair is
                  // putting them back (the fell-out-of-complete recovery, the
                  // material-remap gate reset) -- each targets a card that is
                  // typically far off-screen, so without the scroll the click
                  // does nothing at all.
                  //
                  // The auto-navigator is the one source that must NOT move the
                  // page: it fires on every applied selection, and that is the
                  // "jumped me to the next box" scroll Ryan reported (measured
                  // 2026-08-27: applying Wood & Finish moved the page 496px ->
                  // 1150px onto Width).
                  //
                  // Expressed as an opt-IN rather than a skip so that every
                  // OTHER caller of updateCategoryDisplay -- CATEGORY_TOGGLE,
                  // the tab-change first-incomplete hop, and anything added
                  // later -- is silent in apo_style without having to know this
                  // mode exists. See scrollCategoryIntoView's apo_style branch.
                  const apoScrollAllowed = !isApoAutoNavScrollSuppressed(
                    source,
                    trigger
                  );
                  uiUpdater.updateCategoryDisplay(categoryId, {
                    skipScroll,
                    apoScrollAllowed,
                  });

                  // Publish completion event for coordination tracking
                  this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_OPENED, {
                    categoryId,
                    source: source || CONSTANTS.EVENT_SOURCES.AUTO_NAVIGATOR,
                    reason: reason || 'unknown',
                    animated: true,
                    timestamp: Date.now(),
                  });
                } else {
                  // 🔥 STRICT NO FALLBACK: Fatal module dependency failure — surface loudly, no silent recovery
                  window.MurphyDoorConfigurator.utils.handleSystemError({
                    type: 'ModuleDependencyMissing',
                    error: new Error(
                      'uiUpdater.updateCategoryDisplay not available'
                    ),
                    message:
                      'Cannot open category — uiUpdater dependency missing. Fatal module loading failure.',
                    source: 'navigationManager.handleCategoryOpenRequest',
                    recoverable: false,
                    impact: 'high',
                    details: { categoryId },
                  });
                }
              } catch (error) {
                isDev() &&
                  console.error(
                    '❌ NAVIGATION MANAGER: category_open_request handler failed',
                    error
                  );
              }
            }
          )
        );

        // 🎯 AUTO-NAVIGATION: Listen for gate open requests from auto-navigator.
        // Gate cards (e.g. "Cabinets?") are synthetic question cards, not real
        // categories -- they carry data-category-gate, never data-category, so
        // they don't go through uiUpdater.updateCategoryDisplay(). Scroll the
        // gate card into view so it becomes the visible next stop in the
        // sequence, same as a real category would.
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.UI_GATE_OPEN_REQUEST,
            (eventData) => {
              try {
                const { gateId, source, reason, trigger } = eventData || {};
                if (!gateId) {
                  isDev() &&
                    console.warn(
                      '🎯 NAVIGATION MANAGER: gate_open_request missing gateId, ignoring'
                    );
                  return;
                }
                const gateElement = document.querySelector(
                  `[data-category-gate="${CSS.escape(gateId)}"]`
                );
                if (!gateElement) {
                  isDev() &&
                    console.warn(
                      `🎯 NAVIGATION MANAGER: gate_open_request could not find element for gateId="${gateId}"`
                    );
                  return;
                }

                // Mirror updateCategoryDisplay()'s close-all-others/open-
                // target pattern for gate cards (data-open, not the
                // CATEGORY_OPEN class real categories use -- gate cards
                // never carry data-category, see createGateCardElement).
                // Without this every gate card's options render fully
                // expanded from page load, not just the current one.
                document
                  .querySelectorAll('[data-category-gate]')
                  .forEach((el) => {
                    if (el !== gateElement) {
                      el.setAttribute('data-open', 'false');
                      el.removeAttribute('aria-current');
                      // Only touch aria-expanded if applyGateAnsweredState()
                      // already put it there (the answered/reopenable-
                      // disclosure convention) -- an unanswered, never-
                      // current gate correctly has NO aria-expanded at all
                      // per that function, and adding one here would
                      // contradict that convention instead of matching it.
                      if (el.hasAttribute('aria-expanded'))
                        el.setAttribute('aria-expanded', 'false');
                    }
                  });
                gateElement.setAttribute('data-open', 'true');
                // The current, unanswered, options-visible gate needs an AT
                // signal that this step just became current -- but it's a
                // plain, role-less <div> in this state (createGateCardElement
                // never sets role/tabindex, and applyGateAnsweredState only
                // adds role="button" once ANSWERED, since only then is the
                // card itself a click target -- its unanswered child option
                // buttons are the only clickable elements here). aria-expanded
                // requires a supporting interactive role to mean anything to
                // AT (a generic-role div is inert for it) -- setting it here
                // would be exactly the "looks fixed in DevTools, does nothing
                // for AT" bug this fix is supposed to close. aria-current
                // ="step" carries the same "this is the active step" meaning
                // without requiring a role this element doesn't have. Second
                // gauntlet pass caught this (semantic-html, 2026-08-13) after
                // the first aria-expanded attempt (accessibility +
                // mobile-responsive-ux) missed the missing-role problem.
                // Exactly ONE element may claim aria-current="step". The
                // sweep above only clears other GATES, and the cross-type
                // sweep below (which handles real categories) is skipped in
                // apo_style because cards never collapse there -- so a real
                // category that had been the current step kept its
                // aria-current and TWO elements claimed the step at once.
                // Live-caught 2026-08-17 by counting [aria-current] in the
                // rendered DOM. Clearing here is cheap and mode-independent,
                // and deliberately sits OUTSIDE that sweep so it cannot be
                // disabled along with the collapse behaviour again.
                document
                  .querySelectorAll(
                    '.murphy-door-configurator__category-button[data-category][aria-current]'
                  )
                  .forEach((el) => el.removeAttribute('aria-current'));

                gateElement.setAttribute('aria-current', 'step');

                // Cross-type close: a real category can be left open (its own
                // CATEGORY_OPEN class + data-open) when auto-nav jumps to a
                // gate instead -- the sweep above only resets OTHER gates.
                // Without this, reopening/answering a gate while a real
                // category is current left two panels expanded at once.
                // Gauntlet finding 2026-08-13. MUST close the thumbnails via
                // Animations.slideClose (not a raw classList.remove) so any
                // pending slideOpen timer for this category's animationKey
                // gets cancelled too -- a second gauntlet pass caught this
                // block reintroducing the exact stale-timer race the
                // animationKey guard elsewhere in this PR was built to close.
                // apo_style never folds a card (Ryan review 2026-08-17), so
                // this sweep must not run there. It is what produced the
                // live-reported "selecting a jamb depth collapses all prev
                // cards": in APO 1:1 order Jamb Depth is immediately followed
                // by gate:swing, so applying Jamb Depth auto-navigates to a
                // GATE, this handler fires, and every open real category above
                // it slams shut at once. Nothing in updateCategoryDisplay /
                // updateAppliedState could have prevented it -- the close came
                // from the gate-open path, not the selection path.
                const _apoConductor = window.MurphyDoorConfigurator.conductor;
                const _isApoStyle =
                  _apoConductor?._getConfiguratorExperience?.() === 'apo_style';
                const uiUpdaterAnimations =
                  window.MurphyDoorConfigurator.uiUpdaterHelpers?.Animations;
                (_isApoStyle
                  ? []
                  : document.querySelectorAll(
                      '.murphy-door-configurator__category-button--open[data-category]'
                    )
                ).forEach((el) => {
                  el.classList.remove(
                    'murphy-door-configurator__category-button--open'
                  );
                  el.setAttribute('data-open', 'false');
                  this._setCategoryExpandedState(el, false);
                  const openCategoryId = el.getAttribute('data-category');
                  const thumbs = el.querySelector(
                    '.murphy-door-configurator__thumbnails'
                  );
                  if (thumbs && uiUpdaterAnimations) {
                    uiUpdaterAnimations.slideClose(
                      thumbs,
                      'category-slide:' + openCategoryId
                    );
                  } else if (thumbs) {
                    thumbs.classList.remove(
                      'murphy-door-configurator__thumbnails--open'
                    );
                  }
                });

                isDev() &&
                  console.log(
                    `📁 NAVIGATION MANAGER: Scrolling to gate: ${gateId} (source: ${source || 'unknown'}, reason: ${reason || 'unknown'})`
                  );

                // On page load (fresh_load/restoration), skip the scroll --
                // same rationale as the category-open handler above, including
                // the apo_style auto-navigator suppression. A gate card
                // ("Which way does it swing?") is just as much a "next box in
                // line" as a real category: in APO 1:1 order gate:swing sits
                // directly after Jamb Depth, so answering Jamb Depth used to
                // fling the page down to it.
                //
                // The material-remap reset publishes this SAME event with its
                // own source, so its recovery scroll still fires -- that one is
                // the whole point of that code path.
                // `trigger` is passed for the same reason as in the category
                // handler: a disabled Add to Cart click must scroll even though
                // it carries the auto-navigator source. A gate is just as
                // likely to be the unanswered thing blocking the cart as a real
                // category, so exempting one and not the other would make the
                // button silently do nothing whenever a gate is what's missing.
                const skipScroll =
                  trigger === 'fresh_load' ||
                  trigger === 'restoration' ||
                  isApoAutoNavScrollSuppressed(source, trigger);
                if (!skipScroll) {
                  gateElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }

                this.publishEvent(CONSTANTS.EVENTS.UI_GATE_OPENED, {
                  gateId,
                  source: source || CONSTANTS.EVENT_SOURCES.AUTO_NAVIGATOR,
                  reason: reason || 'unknown',
                  timestamp: Date.now(),
                });
              } catch (error) {
                isDev() &&
                  console.error(
                    '❌ NAVIGATION MANAGER: gate_open_request handler failed',
                    error
                  );
              }
            }
          )
        );

        // 🎯 FIX: Ensure manual category collapse is handled when auto-navigation is disabled.
        // When a user manually closes a completed category, auto-navigation-coordinator may not
        // act, but the UI must still reflect the collapse. This subscription ensures the category's
        // DOM state is correctly updated by this module, which owns the UI.
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.UI_CLOSE_CATEGORY_ICON_CLICKED,
            (eventData) => {
              try {
                const { categoryId } = eventData || {};
                if (!categoryId) return;
                isDev() &&
                  console.log(
                    `📁 NAVIGATION MANAGER: Collapsing X-closed category: ${categoryId}`
                  );
                this.collapseCategory(categoryId);
              } catch (error) {
                isDev() &&
                  console.error(
                    '❌ NAVIGATION MANAGER: close_category_icon handler failed',
                    error
                  );
              }
            }
          )
        );

        // 🎯 CCP COMPLIANT: Listen for composite thumbnail generation requests (event-driven decoupling)
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.UI_COMPOSITE_THUMBNAIL_GENERATION_REQUESTED,
            (eventData) => {
              const startTime = window.performance
                ? window.performance.now()
                : Date.now();
              const METHOD_SIGNATURE =
                'navigationManager.handleCompositeThumbnailRequest';

              try {
                // Validation: Check required event data
                if (!eventData) {
                  throw new Error(
                    'Missing event data for composite thumbnail generation'
                  );
                }

                const { compositeProduct, categoryId, index, requestId } =
                  eventData;

                if (!compositeProduct) {
                  throw new Error('Missing compositeProduct in event data');
                }
                if (categoryId === undefined || categoryId === null) {
                  throw new Error('Missing categoryId in event data');
                }
                if (index === undefined || index === null) {
                  throw new Error('Missing index in event data');
                }
                if (!requestId) {
                  throw new Error('Missing requestId in event data');
                }

                isDev() &&
                  console.log(
                    '🎯 NAVIGATION MANAGER: Processing composite thumbnail generation request',
                    { requestId, categoryId, compositeId: compositeProduct.id }
                  );

                // Call existing method (internal event-driven call)
                const html = this.generateCompositeProductThumbnailHTML(
                  compositeProduct,
                  categoryId,
                  index
                );

                // Track performance
                const executionTime = window.performance
                  ? (window.performance.now() - startTime).toFixed(2)
                  : 'N/A';

                // Publish success event
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_COMPOSITE_THUMBNAIL_GENERATED,
                  {
                    html: html,
                    requestId: requestId,
                    compositeProduct: compositeProduct,
                    categoryId: categoryId,
                    index: index,
                    executionTime: executionTime,
                    timestamp: Date.now(),
                    source: METHOD_SIGNATURE,
                  }
                );

                isDev() &&
                  console.log(
                    `✅ NAVIGATION MANAGER: Composite thumbnail generated (${executionTime}ms)`,
                    { requestId }
                  );
              } catch (error) {
                // Publish failure event
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_COMPOSITE_THUMBNAIL_GENERATION_FAILED,
                  {
                    error: error.message || error,
                    stack: error.stack,
                    requestId: eventData?.requestId,
                    timestamp: Date.now(),
                    source: METHOD_SIGNATURE,
                  }
                );

                isDev() &&
                  console.error(
                    '❌ NAVIGATION MANAGER: Composite thumbnail generation failed',
                    error
                  );
              }
            }
          )
        );
        // 🎯 SIMPLIFIED MODE: Stamp data-simplified-visible on the 3 user-facing categories.
        // Two paths depending on whether buildCategories() has already run:
        // Path A (event fires first): allowlist cached; createCategoryElement() stamps on build.
        // Path B (buildCategories() ran first): iterate _tabToCategoryElementsMap and stamp now.
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY,
            (eventData) => {
              try {
                const categoryIds = eventData?.categoryIds || [];
                if (!Array.isArray(categoryIds) || categoryIds.length === 0)
                  return;

                simplifiedCategoryAllowlist = new Set(categoryIds);

                isDev() &&
                  console.log(
                    '[NavigationManager] Simplified mode categories ready:',
                    [...simplifiedCategoryAllowlist]
                  );

                // Path B: buildCategories() already ran — stamp existing DOM elements imperatively
                let pathBStamped = 0;
                document
                  .querySelectorAll(
                    '.murphy-door-configurator__category-button[data-category]'
                  )
                  .forEach((el) => {
                    const catId = el.getAttribute('data-category');
                    if (catId && simplifiedCategoryAllowlist.has(catId)) {
                      el.setAttribute('data-simplified-visible', 'true');
                      pathBStamped++;
                    }
                  });
                isDev() &&
                  console.log(
                    '[NavigationManager] Simplified mode Path B stamped',
                    pathBStamped,
                    'elements'
                  );

                // Auto-activate the My Door tab so the 3 simplified categories are immediately visible
                const myDoorTab = document.querySelector(
                  '[data-tab="my-door"]'
                );
                if (myDoorTab) {
                  this.activateTab('my-door');
                  isDev() &&
                    console.log(
                      '[NavigationManager] Simplified mode: activated My Door tab'
                    );

                  // Pre-expand ALL simplified categories simultaneously so the user sees all 3
                  // open and ready to interact with on page load (no click-to-expand needed).
                  // Uses expandCategory (not updateCategoryDisplay) to avoid accordion collapse —
                  // updateCategoryDisplay closes all other categories before opening the target.
                  // showCategoryContent() is called for each to initialize slider-based categories
                  // (e.g. Width) that need the drag slider rendered before interaction.
                  const allSimplifiedEls = Array.from(
                    document.querySelectorAll(
                      '.murphy-door-configurator__category-button[data-simplified-visible="true"]'
                    )
                  );
                  if (allSimplifiedEls.length > 0) {
                    // Small defer so the tab activation paint completes first.
                    setTimeout(() => {
                      const uiUpdater = window.MurphyDoorConfigurator.uiUpdater;
                      allSimplifiedEls.forEach((catEl) => {
                        const catId = catEl.getAttribute('data-category');
                        if (!catId) return;
                        // Initialize content (renders drag slider for measurement categories)
                        if (
                          uiUpdater &&
                          typeof uiUpdater.showCategoryContent === 'function'
                        ) {
                          uiUpdater.showCategoryContent(catId);
                        }
                        // Expand panel without collapsing sibling categories
                        this.expandCategory(catId);
                      });
                      simplifiedAllPreExpanded = true;

                      // Inject "Build My Door" commit button after the last simplified
                      // category element. All 3 categories stay in BROWSING state until
                      // this button is clicked — it bulk-applies them all at once.
                      const _lastCatEl =
                        allSimplifiedEls[allSimplifiedEls.length - 1];
                      if (
                        _lastCatEl &&
                        !document.getElementById('md-build-my-door-btn')
                      ) {
                        const _buildBtn = document.createElement('button');
                        _buildBtn.id = 'md-build-my-door-btn';
                        _buildBtn.type = 'button';
                        _buildBtn.className =
                          'md-simplified__build-my-door-btn';
                        _buildBtn.textContent = 'Build My Door';
                        // No aria-label: the visible text "Build My Door" IS the accessible
                        // name. A differing aria-label would violate WCAG 2.5.3 (Label in
                        // Name) and break voice-control activation ("click Build My Door").
                        _lastCatEl.parentNode.insertBefore(
                          _buildBtn,
                          _lastCatEl.nextSibling
                        );
                        _buildBtn.addEventListener('click', () => {
                          window.MurphyDoorConfigurator.CoreEventBus.publish(
                            CONSTANTS.EVENTS
                              .UI_SIMPLIFIED_BUILD_MY_DOOR_CLICKED,
                            {}
                          );
                        });
                      }

                      isDev() &&
                        console.log(
                          '[NavigationManager] Simplified mode: pre-expanded all categories + injected Build My Door button:',
                          allSimplifiedEls
                            .map((el) => el.getAttribute('data-category'))
                            .filter(Boolean)
                        );
                    }, 50);
                  }
                }
              } catch (error) {
                isDev() &&
                  console.error(
                    '[NavigationManager] BUSINESS_SIMPLIFIED_MODE_CATEGORIES_READY handler failed:',
                    error
                  );
              }
            }
          )
        );

        // 🎯 SIMPLIFIED MODE: Auto-advance to next incomplete simplified category after each
        // selection. state:category_completed is not always published (depends on state manager
        // change-detection logic), so we use state:progress_updated which fires reliably after
        // every applied selection and includes categoryId in its payload.
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.STATE_PROGRESS_UPDATED,
            (eventData) => {
              try {
                if (simplifiedCategoryAllowlist === null) return; // not in simplified mode
                if (simplifiedAllPreExpanded) return; // all cats pre-expanded; no accordion advance needed
                const detail = eventData?.detail || eventData;
                const changedCategoryId = detail?.categoryId;
                if (
                  !changedCategoryId ||
                  !simplifiedCategoryAllowlist.has(changedCategoryId)
                )
                  return;

                // Defer so the UI updater has time to stamp --completed on the DOM element
                // before we query it. The UI update (via UI_UPDATE_REQUESTED) runs in the
                // same event-loop tick as state:progress_updated but subscriber order is not
                // guaranteed, so a small defer is the safe cross-module approach.
                setTimeout(() => {
                  const changedEl = document.querySelector(
                    `.murphy-door-configurator__category-button[data-category="${CSS.escape(changedCategoryId)}"]`
                  );
                  if (
                    !changedEl?.classList.contains(
                      'murphy-door-configurator__category-button--completed'
                    )
                  )
                    return;

                  // Find next simplified category in DOM order that is not yet completed
                  const allSimplified = Array.from(
                    document.querySelectorAll(
                      '.murphy-door-configurator__category-button[data-simplified-visible="true"]'
                    )
                  );
                  const nextCategory = allSimplified.find((el) => {
                    const catId = el.getAttribute('data-category');
                    return (
                      catId !== changedCategoryId &&
                      !el.classList.contains(
                        'murphy-door-configurator__category-button--completed'
                      )
                    );
                  });

                  if (nextCategory) {
                    const nextCatId =
                      nextCategory.getAttribute('data-category');
                    // Use updateCategoryDisplay so showCategoryContent() fires and slider
                    // categories render the drag slider, not product thumbnails.
                    const uiUpdater = window.MurphyDoorConfigurator.uiUpdater;
                    if (
                      uiUpdater &&
                      typeof uiUpdater.updateCategoryDisplay === 'function'
                    ) {
                      uiUpdater.updateCategoryDisplay(nextCatId, {
                        skipScroll: true,
                      });
                    } else {
                      this.expandCategory(nextCatId);
                    }
                    isDev() &&
                      console.log(
                        '[NavigationManager] Simplified mode: auto-advanced to next category:',
                        nextCatId
                      );
                  }
                }, 150);
              } catch (error) {
                isDev() &&
                  console.error(
                    '[NavigationManager] STATE_PROGRESS_UPDATED simplified handler failed:',
                    error
                  );
              }
            }
          )
        );

        // 🎯 SIMPLIFIED MODE: Reveal full configurator once all defaults have been applied.
        // 1. Clear simplifiedCategoryAllowlist so normal showCategoriesForTab logic resumes.
        // 2. Mark the tools div "complete" so CSS hides My Door tab and shows dynamic tabs.
        // 3. Clear any inline display:none on category buttons.
        //
        // NOTE on tab navigation: this subscriber used to call handleTabTransition
        // synchronously to the first product tab. That raced the simplified-mode
        // coordinator's staggered celebration cascade (setTimeout 250ms confetti +
        // setTimeout 1500ms Summary tab nav) because BUSINESS_SIMPLIFIED_MODE_COMPLETE
        // subscribers all fire on the same tick — the tab swap landed at T+0ms,
        // hiding the progress bar fill animation and confetti before the user
        // could perceive them. Tab navigation now flows through the coordinator's
        // UI_TAB_OPEN_REQUEST publish at T+1500ms instead. See ccp-cascade-logic-
        // auditor report 2026-06-18.
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_COMPLETE,
            () => {
              try {
                isDev() &&
                  console.log(
                    '[NavigationManager] SIMPLIFIED_MODE_COMPLETE — transitioning to full configurator'
                  );

                // Clear allowlist so showCategoriesForTab reverts to normal tab-based logic
                simplifiedCategoryAllowlist = null;
                simplifiedAllPreExpanded = false;

                // Mark complete — CSS swaps My Door tab for full dynamic tabs
                const toolsEl = document.querySelector(
                  '.murphy-door-configurator__tools[data-simplified-mode]'
                );
                if (toolsEl)
                  toolsEl.setAttribute('data-simplified-mode', 'complete');

                // Clear any inline display:none on all category buttons.
                //
                // 🚨 This sweep is deliberately GATE-BLIND, and it MUST stay
                // that way -- it exists to undo simplified mode's own hiding
                // and has no business re-deriving the "Cabinets?" answer.
                // Duplicating that rule here is exactly the DRY violation the
                // publish below avoids: md-config-ui-gate-visibility-manager.js
                // is the single owner of gate-driven visibility, so this
                // announces "categories were shown" and lets the owner
                // re-assert its verdict.
                //
                // Without the announce, the sweep un-hides Top/Bottom Cabinet
                // Doors that the gate hid while "Cabinets?" is UNANSWERED, and
                // nothing puts them back -- measured live on production
                // 2026-08-31 via a ?config= URL restore:
                //   6126 ui:categories_shown_for_tab   "" -> "none"   (gate hides)
                //   6375 business:simplified_mode_complete "none" -> "" (this sweep)
                // The customer then sees cabinet + knobs cards for a question
                // they never answered. Same class as the bug already fixed in
                // md-config-ui-updater.js's updateCategoryVisibility() sweep,
                // which carries the identical publish for the identical reason.
                const clearedCategoryEls = document.querySelectorAll(
                  '.murphy-door-configurator__category-button[data-category]'
                );
                clearedCategoryEls.forEach((el) => {
                  el.style.display = '';
                });

                // Re-assert progressive-disclosure ownership after the sweep.
                // tabId is null because apo_style has no tab bar and this
                // handler is not tab-scoped -- the gate manager treats the
                // event as "re-apply your rules", not as tab metadata.
                this.publishEvent(
                  CONSTANTS.EVENTS.UI_CATEGORIES_SHOWN_FOR_TAB,
                  {
                    tabId: null,
                    categoryCount: clearedCategoryEls.length,
                    timestamp: Date.now(),
                    source: 'navigationManager.simplifiedModeComplete',
                  }
                );

                // Tab navigation deliberately omitted — see comment block above.
              } catch (error) {
                isDev() &&
                  console.error(
                    '[NavigationManager] SIMPLIFIED_MODE_COMPLETE handler failed:',
                    error
                  );
              }
            }
          )
        );

        // SIMPLIFIED MODE: ATC validation — flash incomplete categories with error styling.
        // When the user clicks the disabled Add to Cart button while in simplified mode
        // (not all 3 categories applied), highlight the incomplete categories instead of
        // navigating (all categories are already visible; navigation is not needed).
        cleanupHandlers.push(
          window.MurphyDoorConfigurator.CoreEventBus.subscribe(
            CONSTANTS.EVENTS.UI_ADD_TO_CART_DISABLED_CLICKED,
            (eventData) => {
              try {
                if (simplifiedCategoryAllowlist === null) return; // not in simplified mode

                // Build My Door validation passes specific missing IDs; ATC-disabled
                // path has no payload and falls back to --completed class check.
                const missingIds = eventData?.missingCategoryIds || null;
                const incomplete = Array.from(
                  document.querySelectorAll(
                    '.murphy-door-configurator__category-button[data-simplified-visible="true"]'
                  )
                ).filter((el) => {
                  if (missingIds) {
                    return missingIds.includes(
                      el.getAttribute('data-category')
                    );
                  }
                  return !el.classList.contains(
                    'murphy-door-configurator__category-button--completed'
                  );
                });

                if (incomplete.length === 0) return;

                const MISSING_CLASS =
                  'murphy-door-configurator__category-button--simplified-missing';

                incomplete.forEach((el) => {
                  el.classList.remove(MISSING_CLASS); // reset to re-trigger animation
                  // Force reflow so animation restarts even if class was already present
                  void el.offsetWidth; // eslint-disable-line no-void
                  el.classList.add(MISSING_CLASS);
                  setTimeout(() => el.classList.remove(MISSING_CLASS), 600);
                });

                // The shake/highlight is visual-only — announce to assistive tech so
                // screen reader users learn why "Build My Door" did nothing (WCAG 4.1.3).
                let _srStatus = document.getElementById(
                  'md-simplified-sr-status'
                );
                if (!_srStatus) {
                  _srStatus = document.createElement('div');
                  _srStatus.id = 'md-simplified-sr-status';
                  _srStatus.className = 'visually-hidden';
                  _srStatus.setAttribute('role', 'alert');
                  _srStatus.setAttribute('aria-live', 'assertive');
                  document.body.appendChild(_srStatus);
                }
                _srStatus.textContent =
                  'Please choose an option for all ' +
                  incomplete.length +
                  (incomplete.length === 1
                    ? ' highlighted selection'
                    : ' highlighted selections') +
                  ' before building your door.';

                isDev() &&
                  console.log(
                    '[NavigationManager] Simplified mode ATC validation — highlighted',
                    incomplete.length,
                    'incomplete categories'
                  );
              } catch (error) {
                isDev() &&
                  console.error(
                    '[NavigationManager] ATC disabled simplified validation failed:',
                    error
                  );
              }
            }
          )
        );
      } else {
      }

      // ✅ EVENT-DRIVEN ARCHITECTURE: Trust event system (no polling backup needed)
      // Event-based re-rendering via system:state_initialized is sufficient
      isDev() &&
        console.log(
          '🎯 NAVIGATION MANAGER: Using event-driven re-rendering only (no polling)'
        );
    },

    /**
     * Re-render placeholder thumbnails with actual product data.
     * Called when productStructure becomes available (SYSTEM_STATE_INITIALIZED).
     *
     * PERFORMANCE FIX (P0): Only hydrates placeholders for the CURRENTLY VISIBLE
     * tab's categories. Hidden tabs retain their lightweight placeholders until
     * the user navigates to them (handled by showCategoriesForTab -> _hydrateVisiblePlaceholders).
     * This prevents 1,000+ images from being injected into the DOM simultaneously.
     */
    reRenderPlaceholderThumbnails() {
      // Only hydrate the active tab's categories (visible ones)
      // Find which tab is currently active
      const DOM = getDOM();
      if (!DOM) return;

      const activeTab = DOM.getElement(
        '.murphy-tab-button.active, .murphy-tab-button--active'
      );
      const activeTabId = activeTab ? activeTab.getAttribute('data-tab') : null;

      if (!activeTabId || !(this._tabToCategoryElementsMap instanceof Map)) {
        isDev() &&
          console.warn(
            'NAVIGATION MANAGER: No active tab or category map not ready for placeholder hydration'
          );
        return;
      }

      const visibleCategories =
        this._tabToCategoryElementsMap.get(activeTabId) || [];

      if (visibleCategories.length === 0) {
        return;
      }

      isDev() &&
        console.log(
          'NAVIGATION MANAGER: Hydrating placeholders for active tab "' +
            activeTabId +
            '" (' +
            visibleCategories.length +
            ' categories)'
        );

      // Delegate to the shared on-demand hydration method
      this._hydrateVisiblePlaceholders(visibleCategories);
    },

    /**
     * LEGACY COMPAT: Keep the old full-DOM hydration available but gated.
     * Only used if explicitly needed (e.g., URL recovery restoring a non-first tab).
     * @private
     */
    _reRenderAllPlaceholderThumbnails() {
      const placeholders = document.querySelectorAll(
        '.murphy-door-configurator__loading-placeholder'
      );

      if (placeholders.length === 0) {
        return;
      }

      placeholders.forEach((placeholder) => {
        const categoryId = placeholder.getAttribute('data-category');
        if (categoryId) {
          const DOM = getDOM();
          const categoryElement = DOM
            ? DOM.getElement(
                // BUTTON, not a category LAYER -- a bare [data-category] also
                // matches the composited image layers, which hold no
                // .thumbnails, so the placeholder went unfilled.
                '.murphy-door-configurator__category-button[data-category="' +
                  categoryId +
                  '"]'
              ) || DOM.getElement('[data-category="' + categoryId + '"]')
            : null;
          const thumbnailsContainer = categoryElement?.querySelector(
            '.murphy-door-configurator__thumbnails'
          );

          if (thumbnailsContainer) {
            const conductor = window.MurphyDoorConfigurator.conductor;
            if (conductor && conductor.getVirtualizedProductsForCategory) {
              try {
                const virtualizedProducts =
                  conductor.getVirtualizedProductsForCategory(categoryId);

                if (virtualizedProducts && virtualizedProducts.length > 0) {
                  const freshHTML = this.generateThumbnailsHTMLWithProducts(
                    categoryId,
                    virtualizedProducts
                  );

                  if (freshHTML && freshHTML !== placeholder.outerHTML) {
                    thumbnailsContainer.innerHTML = sanitizeHTML(freshHTML);
                    this.publishEvent(
                      CONSTANTS.EVENTS.UI_PLACEHOLDER_RERENDERED,
                      {
                        categoryId,
                        productCount: virtualizedProducts.length,
                        timestamp: Date.now(),
                        source:
                          'navigationManager._reRenderAllPlaceholderThumbnails',
                      }
                    );
                  }
                }
              } catch (error) {
                isDev() &&
                  console.error(
                    'NAVIGATION MANAGER: Error re-rendering thumbnails for category "' +
                      categoryId +
                      '":',
                    error
                  );
              }
            }
          } else {
            isDev() &&
              console.warn(
                'NAVIGATION MANAGER: Thumbnails container not found for category "' +
                  categoryId +
                  '"'
              );
          }
        }
      });
    },

    /* Core Navigation Methods */
    /**
     * Handles tab transitions with comprehensive event lifecycle
     * @param {string} fromTabId - The ID of the tab to deactivate
     * @param {string} toTabId - The ID of the tab to activate
     * @fires ui:tab_transition_started
     * @fires ui:tab_transition_failed
     * @fires ui:tab_deactivated
     * @fires ui:tab_activated
     * @fires performance:navigation_transition_slow
     * @fires ui:tab_transition_completed
     * @fires ui:summary_tab_activated
     */
    handleTabTransition(fromTabId, toTabId) {
      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'navigationManager.handleTabTransition';

      try {
        // 1. Publish transition started
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_TRANSITION_STARTED, {
          fromTabId,
          toTabId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // 2. Validation
        if (
          !window.MurphyDoorConfigurator.main.validateTabTransition(
            fromTabId,
            toTabId
          )
        ) {
          this.publishEvent(CONSTANTS.EVENTS.UI_TAB_TRANSITION_FAILED, {
            error: 'Tab transition validation failed',
            fromTabId,
            toTabId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          return;
        }

        // 3. Perform transition - deactivate old tab
        this.deactivateTab(fromTabId);
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_DEACTIVATED, {
          tabId: fromTabId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // 4. Activate new tab
        this.activateTab(toTabId);
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_ACTIVATED, {
          tabId: toTabId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // 5. Show categories for new tab
        this.showCategoriesForTab(toTabId);

        // 6. Track performance
        const executionTime = window.performance.now() - startTime;
        if (executionTime > PERFORMANCE_CONSTANTS.TRANSITION_THRESHOLD_MS) {
          this.publishEvent(
            CONSTANTS.EVENTS.PERFORMANCE_NAVIGATION_TRANSITION_SLOW,
            {
              executionTime: executionTime.toFixed(2),
              threshold: PERFORMANCE_CONSTANTS.TRANSITION_THRESHOLD_MS,
              operation: 'handleTabTransition',
              fromTabId,
              toTabId,
              source: METHOD_SIGNATURE,
              timestamp: Date.now(),
            }
          );

          isDev() &&
            console.warn(
              `⚠️ PERFORMANCE: Tab transition took ${executionTime.toFixed(2)}ms ` +
                `(threshold: ${PERFORMANCE_CONSTANTS.TRANSITION_THRESHOLD_MS}ms)`
            );
        }

        // 7. Instagram optimization event (special case for summary tab)
        if (toTabId === 'summary') {
          isDev() &&
            console.log(
              '🚀 INSTAGRAM OPTIMIZATION: Summary tab activated, requesting cart data prefetch'
            );
          this.publishEvent(CONSTANTS.EVENTS.UI_SUMMARY_TAB_ACTIVATED, {
            source: METHOD_SIGNATURE,
            timestamp: Date.now(),
          });
        }

        // 8. Publish transition completed
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_TRANSITION_COMPLETED, {
          fromTabId,
          toTabId,
          executionTime: executionTime.toFixed(2),
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // 9. Publish tab opened event with source tracking for auto-navigation coordination
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_OPENED, {
          tabId: toTabId,
          source: CONSTANTS.EVENT_SOURCES.USER_CLICK,
          timestamp: Date.now(),
        });

        // Note: publishTabReadinessState() is called within activateTab() and showCategoriesForTab()
        // No additional call needed here to avoid duplicate state publishing
      } catch (error) {
        // 10. Publish transition failed
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_TRANSITION_FAILED, {
          error: error.message || error,
          stack: error.stack,
          fromTabId,
          toTabId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        isDev() &&
          console.error(
            `❌ NAVIGATION MANAGER: Tab transition failed - ${error.message}`
          );
      }
    },

    handleCategoryTransition(categoryId, isOpen) {
      // Get variant state through main
      const inVariantTransition =
        window.MurphyDoorConfigurator.main.getVariantTransitionState();
      if (inVariantTransition) {
        return;
      }

      if (isOpen) {
        const success = this.expandCategory(categoryId);

        // Publish category opened event with source tracking for auto-navigation coordination
        if (success) {
          this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_OPENED, {
            categoryId,
            source: CONSTANTS.EVENT_SOURCES.USER_CLICK,
            timestamp: Date.now(),
          });
        }
      } else {
        this.collapseCategory(categoryId);
      }
    },

    /* Structure Creation */
    /**
     * Creates the navigation structure based on the Perfect Product Dictionary (PPD).
     * Builds tabs and categories according to the PPD data structure.
     *
     * CCP COMPLIANT: Reads from PPD directly via conductor delegation.
     * No processedData parameter needed - all data accessed through conductor methods.
     *
     * @returns {Object} Result object with status information
     * @fires ui:navigation_structure_creation_started
     * @fires ui:navigation_structure_creation_completed
     * @fires ui:navigation_structure_creation_failed
     * @fires performance:navigation_structure_slow
     */
    createNavigationStructure() {
      // PATTERN 6: Bootstrap Runtime Protection - queue if PPD not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            '[NavigationManager] createNavigationStructure queued - bootstrap not complete'
          );
        pendingCalls.push({
          fn: this.createNavigationStructure,
          context: this,
          args: [],
        });
        return { success: false, error: 'Bootstrap not complete, call queued' };
      }

      const startTime = window.performance.now();
      const METHOD_SIGNATURE = 'navigationManager.createNavigationStructure';

      try {
        // 1. Publish operation started
        this.publishEvent(
          CONSTANTS.EVENTS.UI_NAVIGATION_STRUCTURE_CREATION_STARTED,
          {
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          }
        );

        const DOM = getDOM();
        if (!DOM) {
          const errorMsg = ERROR_MESSAGES.DOM_NOT_AVAILABLE;
          isDev() && console.warn(`🎯 NAVIGATION MANAGER: ${errorMsg}`);
          this.publishEvent(
            CONSTANTS.EVENTS.UI_NAVIGATION_STRUCTURE_CREATION_FAILED,
            {
              error: errorMsg,
              timestamp: Date.now(),
              source: METHOD_SIGNATURE,
            }
          );
          return {
            success: false,
            error: errorMsg,
          };
        }

        const tabsContainer = DOM.getElement('.murphy-door-configurator__tabs');
        const summaryTab = DOM.getElement('[data-tab="summary"]');
        const svgTemplates = this.getSvgTemplates();

        if (!this.validateElements(tabsContainer, svgTemplates)) {
          const errorMsg = ERROR_MESSAGES.STRUCTURE_VALIDATION_FAILED;
          this.publishEvent(
            CONSTANTS.EVENTS.UI_NAVIGATION_STRUCTURE_CREATION_FAILED,
            {
              error: errorMsg,
              timestamp: Date.now(),
              source: METHOD_SIGNATURE,
            }
          );
          return {
            success: false,
            error: errorMsg,
          };
        }

        // Compute ordered tab IDs for subscribers (mobile swipe, etc.) before building tabs
        const tabStructure =
          window.MurphyDoorConfigurator.main.getTabStructure();
        const orderedTabIds = [
          ...Array.from(tabStructure.entries())
            .sort(([, a], [, b]) => a.loadOrder - b.loadOrder)
            .map(([tabId]) => tabId),
          'summary',
        ];

        this.buildTabs(tabsContainer, summaryTab, svgTemplates);
        this.buildCategories();

        // CCP COMPLIANT: Publish tab readiness state for reactive synchronization
        this.publishTabReadinessState();

        // 2. Track performance
        const executionTime = window.performance.now() - startTime;
        if (executionTime > PERFORMANCE_CONSTANTS.STRUCTURE_THRESHOLD_MS) {
          this.publishEvent(
            CONSTANTS.EVENTS.PERFORMANCE_NAVIGATION_STRUCTURE_SLOW,
            {
              executionTime: executionTime.toFixed(2),
              threshold: PERFORMANCE_CONSTANTS.STRUCTURE_THRESHOLD_MS,
              operation: 'createNavigationStructure',
              source: METHOD_SIGNATURE,
              timestamp: Date.now(),
            }
          );

          isDev() &&
            console.warn(
              `⚠️ PERFORMANCE: Navigation structure creation took ${executionTime.toFixed(2)}ms ` +
                `(threshold: ${PERFORMANCE_CONSTANTS.STRUCTURE_THRESHOLD_MS}ms)`
            );
        }

        // 3. Publish operation completed (include orderedTabIds for subscribers like mobile swipe)
        this.publishEvent(
          CONSTANTS.EVENTS.UI_NAVIGATION_STRUCTURE_CREATION_COMPLETED,
          {
            executionTime: executionTime.toFixed(2),
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
            orderedTabIds,
            initialActiveTabId: orderedTabIds[0] || null,
          }
        );

        return { success: true };
      } catch (error) {
        // 4. Publish operation failed
        this.publishEvent(
          CONSTANTS.EVENTS.UI_NAVIGATION_STRUCTURE_CREATION_FAILED,
          {
            error: error.message || error,
            stack: error.stack,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          }
        );

        isDev() &&
          console.error(
            `❌ NAVIGATION MANAGER: Navigation structure creation failed - ${error.message}`
          );

        return {
          success: false,
          error:
            error.message || 'Unknown error in navigation structure creation',
        };
      }
    },

    buildTabs(container, summaryTab, svgTemplates) {
      const tabStructure = window.MurphyDoorConfigurator.main.getTabStructure();

      Array.from(tabStructure.entries())
        .sort(([, a], [, b]) => a.loadOrder - b.loadOrder)
        .forEach(([tabId, tabData]) => {
          const tabButton = this.createTabButton(tabId, tabData, svgTemplates);
          container.insertBefore(tabButton, summaryTab);

          window.MurphyDoorConfigurator.main.handleStateChange(
            STATE_ACTIONS.TAB_CHANGED,
            {
              tabId,
              isInitial: true,
            }
          );
        });
    },
    buildCategories() {
      const categoryStructure =
        window.MurphyDoorConfigurator.main.getCategoryStructure();
      const tabStructure = window.MurphyDoorConfigurator.main.getTabStructure();
      const container = document.querySelector(
        '.murphy-door-configurator__categories'
      );

      if (!container) {
        isDev() &&
          console.error(
            'NAVIGATION_MANAGER: Categories container not found in DOM'
          );
        return;
      }

      // 🔥 PPD-FIRST STRICT MODE: Enforce PPD dependency, fail fast on bootstrap errors
      const conductor = window.MurphyDoorConfigurator.conductor;
      const sortedCategories = conductor?.getSortedCategories();

      if (!sortedCategories) {
        throw new Error(
          'CRITICAL: Conductor getSortedCategories() not available - PPD not initialized. This indicates a bootstrap failure.'
        );
      }

      // 🔥 BUILD TAB-TO-CATEGORY-ELEMENTS MAP for O(1) tab activation lookups
      // CRITICAL: This map caches direct DOM element references on initialization.
      // If category elements are ever re-rendered or removed from the DOM, this
      // map MUST be rebuilt to avoid stale references. The isConnected guard in
      // showCategoriesForTab prevents hard errors but categories will silently
      // fail to render. Call buildCategories() to fully rebuild the structure.
      this._tabToCategoryElementsMap = new Map();

      sortedCategories.forEach(([categoryId, categoryData]) => {
        // 🔥 PPD-FIRST: O(1) lookup via conductor delegation to Layer 4 map
        const parentTabId = conductor?.getParentTabForCategory(categoryId);

        if (parentTabId) {
          const categoryElement = this.createCategoryElement(
            categoryId,
            categoryData
          );
          // Add data-tab attribute to associate with parent tab
          categoryElement.setAttribute('data-tab', parentTabId);
          // Initially hide all categories
          categoryElement.style.display = 'none';
          container.appendChild(categoryElement);

          // 🔥 ADD TO TAB-TO-CATEGORY MAP for optimized showCategoriesForTab()
          if (!this._tabToCategoryElementsMap.has(parentTabId)) {
            this._tabToCategoryElementsMap.set(parentTabId, []);
          }
          this._tabToCategoryElementsMap.get(parentTabId).push(categoryElement);
        } else {
          isDev() &&
            console.warn(
              `⚠️ NAVIGATION MANAGER: No parent tab found for category "${categoryId}" - skipping`
            );
        }
      });

      // GATE CARDS (e.g. "Cabinets?"): append-only, after the real PPD-derived
      // loop above -- never alters sortedCategories itself. Hard-gated so the
      // hot path (gate cards off, i.e. classic/dual modes today) is a provable
      // no-op: zero DOM nodes created, zero map entries added.
      if (conductor?._areGateCardsEnabled && conductor._areGateCardsEnabled()) {
        const gateDefs = CONSTANTS.GATE_CARD_DEFINITIONS || [];
        gateDefs.forEach((gateDef) => {
          const parentTabId = conductor?.getParentTabForCategory(
            gateDef.insertBeforeCategory
          );
          if (!parentTabId) {
            isDev() &&
              console.warn(
                `⚠️ NAVIGATION MANAGER: Gate card "${gateDef.id}" target category "${gateDef.insertBeforeCategory}" not found - skipping`
              );
            return;
          }

          const gateElement = this.createGateCardElement(gateDef);
          gateElement.setAttribute('data-tab', parentTabId);
          gateElement.style.display = 'none';

          const targetElement = container.querySelector(
            '[data-category="' + CSS.escape(gateDef.insertBeforeCategory) + '"]'
          );
          if (targetElement) {
            container.insertBefore(gateElement, targetElement);
          } else {
            container.appendChild(gateElement);
          }

          if (!this._tabToCategoryElementsMap.has(parentTabId)) {
            this._tabToCategoryElementsMap.set(parentTabId, []);
          }
          const tabElements = this._tabToCategoryElementsMap.get(parentTabId);
          const targetIndex = targetElement
            ? tabElements.indexOf(targetElement)
            : -1;
          if (targetIndex === -1) {
            tabElements.push(gateElement);
          } else {
            tabElements.splice(targetIndex, 0, gateElement);
          }
        });
      }

      // apo_style: single continuous scroll, no tab bar. Reveal every
      // category (and gate card) immediately instead of leaving them all
      // display:none awaiting a tab click that will never come -- hard-gated
      // so the hot path (classic/dual modes) is unaffected.
      if (conductor?._getConfiguratorExperience?.() === 'apo_style') {
        this._revealAllCategoriesForApoStyle(container);
      }
    },

    /**
     * apo_style has no tabs, so every category built above (still
     * display:none from the loop that partitions them by tab) needs to be
     * shown at once, in the SAME order Daniel confirmed: each tab's
     * categories in their existing internal order, tabs concatenated in
     * their existing display order (Appearance, then Features, then Specs
     * -- "append features and other tabs to the bottom of the appearance
     * settings"). Reuses buildTabs()'s own loadOrder sort so this can never
     * drift from the order a user would see today in classic/dual modes --
     * do not re-derive tab order a second way.
     *
     * No curated-defaults wait: apo_style is deliberately NOT added to
     * isCuratedEnabled() (confirmed with Daniel 2026-08-11), so
     * BUSINESS_CURATED_DEFAULTS_APPLIED never fires for it -- reveal is
     * synchronous and immediate, same as a manual-start session in classic
     * mode.
     *
     * Hydration reuses the SAME per-category placeholder-to-real-thumbnail
     * function the tabbed path already uses (_hydrateVisiblePlaceholders) --
     * apo_style's ~15-16 categories are far below the 1,000+ that caused
     * the original Murphy Bed eager-load incident, so a single synchronous
     * pass across every category is acceptable here. Revisit with a
     * staggered/viewport-gated hydration budget only if a future apo_style
     * product line grows into that range.
     * @param {HTMLElement} container
     */
    _revealAllCategoriesForApoStyle(container) {
      const conductor = window.MurphyDoorConfigurator.conductor;
      const tabStructure = window.MurphyDoorConfigurator.main.getTabStructure();
      if (!tabStructure || !(this._tabToCategoryElementsMap instanceof Map)) {
        isDev() &&
          console.warn(
            '⚠️ NAVIGATION MANAGER: apo_style reveal-all skipped - tab structure or category map unavailable'
          );
        return;
      }

      const orderedTabIds = Array.from(tabStructure.entries())
        .sort(([, a], [, b]) => a.loadOrder - b.loadOrder)
        .map(([tabId]) => tabId);

      // Flatten in tab-concatenated order first -- this stays the fallback
      // sequence for anything neither order constant below knows about.
      const flattenedElements = [];
      orderedTabIds.forEach((tabId) => {
        const tabElements = this._tabToCategoryElementsMap.get(tabId) || [];
        tabElements.forEach((element) => flattenedElements.push(element));
      });

      // Re-sort into the real APO step sequence when defined. A category
      // (or gate card) absent from the list falls back to the end, in its
      // existing tab-concatenated position -- nothing is ever silently
      // dropped, it just isn't APO-ordered yet.
      // Two presentation modes, chosen by the apo_1to1_mode section setting
      // (conductor._isApo1to1Enabled()): the literal MageWorx step order, or
      // the default clustered order (customization first, measurements +
      // Locking Systems last -- confirmed with Daniel 2026-08-12).
      // Ranking is delegated to conductor._getApoOrderRank so the metaobject's
      // apo_load_order can override the hardcoded constant per category, and
      // -- more importantly -- so this reveal order and the auto-navigation
      // walk in md-config-bl-auto-navigation-coordinator.js can never disagree.
      // Both used to read the constants independently.
      let orderedElements = flattenedElements;
      if (typeof conductor?._getApoOrderRank === 'function') {
        const sortKeyFor = (element) => {
          const gateId = element.getAttribute('data-category-gate');
          if (gateId) return 'gate:' + gateId;
          return element.getAttribute('data-category') || '';
        };
        orderedElements = flattenedElements
          .map((element, originalIndex) => ({ element, originalIndex }))
          .sort((a, b) => {
            const aRank = conductor._getApoOrderRank(sortKeyFor(a.element));
            const bRank = conductor._getApoOrderRank(sortKeyFor(b.element));
            if (aRank !== bRank) return aRank - bRank;
            // Both unranked (or tied) -- preserve original relative order.
            return a.originalIndex - b.originalIndex;
          })
          .map(({ element }) => element);
      }

      const revealedElements = [];
      orderedElements.forEach((element) => {
        element.style.display = '';
        // appendChild on an already-attached node MOVES it, it doesn't
        // clone -- this is what actually produces the final render order.
        container.appendChild(element);
        revealedElements.push(element);
      });

      // NOTE: _hydrateVisiblePlaceholders() is deliberately NOT called here.
      // _forceAllCategoriesOpenForApoStyle() renders every category through
      // showCategoryContent(), which rewrites the same .thumbnails container
      // the placeholder lives in -- so hydrating first just builds ~16 tile
      // grids that are discarded one line later. Worse, each render publishes
      // UI_CATEGORY_CONTENT_RENDERED, which md-config-bl-forbidden-validator.js
      // answers with a FULL preemptive forbidden-rules pass plus a DOM sweep
      // across every category, so the duplicate work fans out well beyond the
      // grid builds themselves. Caught in review 2026-08-17.
      this._forceAllCategoriesOpenForApoStyle(revealedElements);

      this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORIES_SHOWN_FOR_TAB, {
        tabId: null,
        categoryCount: revealedElements.length,
        timestamp: Date.now(),
        source: 'navigationManager._revealAllCategoriesForApoStyle',
      });

      isDev() &&
        console.log(
          `[NavigationManager] apo_style: revealed ${revealedElements.length} categories across ${orderedTabIds.length} tabs in one scroll`
        );
    },

    /**
     * apo_style has no collapsed state at all -- not "collapsed until you
     * reach it", not "collapses once answered". Daniel 2026-08-17: "we dont
     * want cards to be closed at all they should always be open ... it
     * shouldnt have the expandablity it just sits open."
     *
     * Reveal alone is not enough: _revealAllCategoriesForApoStyle only clears
     * `display`, so every card still lands in its default CLOSED state
     * (max-height:0 via .thumbnails without --open) and stays that way until
     * auto-navigation opens it one at a time. This pass opens all of them up
     * front.
     *
     * Slider categories need special handling: _hydrateVisiblePlaceholders
     * deliberately SKIPS them, because injecting thumbnail HTML makes Width/
     * Height render a product tile instead of the drag slider. Their real
     * content only appears via showCategoryContent() -> renderSlider(), so we
     * ask for it explicitly here -- otherwise those cards would sit open and
     * empty.
     *
     * @param {HTMLElement[]} elements - the revealed category/gate elements
     */
    _forceAllCategoriesOpenForApoStyle(elements) {
      if (!Array.isArray(elements) || elements.length === 0) return;
      const uiUpdater = window.MurphyDoorConfigurator.uiUpdater;

      elements.forEach((element) => {
        if (!element || !element.isConnected) return;

        // The --open CLASS is applied to gate cards too, BEFORE the gate
        // early-return below. In apo_style it is purely cosmetic: apo-mode
        // neutralises its border (section-md-apo-mode.css -- the dark outline
        // comes from aria-current='step' instead), and its only other effect
        // is opening `.thumbnails`, which a gate card does not have. What it
        // actually carries here is the header underline + drop shadow
        // (section-md-configurator.css, `--open .category-title-wrapper`).
        //
        // Skipping gates entirely is what made "Cabinets?" the one card with
        // no rule under its title and no shadow, while all 16 real cards had
        // both -- measured live on staging 2026-08-28. The 2026-08-17 skip was
        // about the data-open ATTRIBUTE fighting the gate sweeps; it took the
        // styling class down with it as an unintended side effect.
        element.classList.add(
          'murphy-door-configurator__category-button--open'
        );

        // Gate cards are otherwise left alone here. They manage their own
        // answered state, and two ungated sweeps (the UI_GATE_OPEN_REQUEST
        // handler above and its twin in md-config-ui-gate-visibility-manager.js)
        // own their data-open, resetting every non-current gate to "false".
        // Forcing data-open="true" here just fought those sweeps and left the
        // attribute contradicting itself. It was never load-bearing anyway:
        // section-md-apo-mode.css shows gate options in this mode regardless
        // of data-open, so gate cards stay expanded either way. Caught in
        // review 2026-08-17.
        if (element.hasAttribute('data-category-gate')) return;

        element.setAttribute('data-open', 'true');

        // aria-expanded is REMOVED, not set to "true". On a role="button" it
        // advertises a collapse action, and in apo_style there isn't one --
        // the card can never contract, and Enter/Space now resets the
        // selection instead. Announcing "button, expanded" for a control that
        // cannot collapse is worse than announcing nothing. This matches the
        // conclusion md-config-ui-gate-visibility-manager.js already reached
        // for gate cards (it drops aria-expanded and relies on
        // aria-current="step"); real categories simply never got updated to
        // match. Caught in review 2026-08-17.
        // Since 2026-09-28 the attribute lives on the header's toggle button,
        // not the card; clear both so a stale card-level one cannot linger.
        element.removeAttribute('aria-expanded');
        this._setCategoryExpandedState(element, null);

        const categoryId = element.getAttribute('data-category');
        if (!categoryId) return;

        // Render through showCategoryContent() for EVERY category, not just
        // sliders. _hydrateVisiblePlaceholders only knows how to inject
        // product-level thumbnail HTML, which is correct solely for
        // multi-product categories (displayMode 'MIX'). It is wrong for:
        //   - LONELY_MULTI_VARIANT (one composite product whose VARIANTS are
        //     the real options -- 'Top Knobs & Pulls' is one product with 62
        //     variants, so product-level HTML renders a single tile labelled
        //     with the category name and nothing selectable)
        //   - sliders (Width/Height), which need renderSlider()
        // showCategoryContent() is the function that dispatches on
        // displayMode, so it is the only correct renderer for all of them.
        //
        // Live-confirmed 2026-08-17 off the PPD: Top Knobs & Pulls =
        // LONELY_MULTI_VARIANT / 1 product / 62 variants, Bottom Cabinet
        // Doors = MIX / 9 products. Exactly matches which cards rendered and
        // which didn't (Daniel: "the knobs and pulls are just not showing up
        // at all"). Categories opened by auto-navigation looked fine because
        // updateCategoryDisplay() already routes through here -- only cards
        // never visited, like knobs behind a cabinet dependency, stayed broken.
        if (typeof uiUpdater?.showCategoryContent === 'function') {
          try {
            uiUpdater.showCategoryContent(categoryId);
          } catch (error) {
            // Never let one category's render failure abort the rest of the
            // open-all pass -- a single empty card is far better than every
            // card below it staying closed.
            isDev() &&
              console.error(
                `[NavigationManager] apo_style: content render failed for "${categoryId}"`,
                error
              );
          }
        }

        const thumbnails = element.querySelector(
          '.murphy-door-configurator__thumbnails'
        );
        if (thumbnails) {
          thumbnails.classList.add(
            'murphy-door-configurator__thumbnails--open'
          );
        }

        // Safety net for skipping _hydrateVisiblePlaceholders on this path.
        // Every displayMode branch of showCategoryContent() rewrites the
        // .thumbnails container, which is where the "Loading thumbnails..."
        // placeholder lives, so it is normally removed as a side effect. But
        // if a render throws (caught above) or bails early, the placeholder
        // would be left on screen inside a permanently-open card, reading as
        // a card stuck loading forever. Clear it explicitly: an empty card is
        // honest, a card claiming to still be loading is not.
        const stalePlaceholder = element.querySelector(
          '.murphy-door-configurator__loading-placeholder'
        );
        if (stalePlaceholder) stalePlaceholder.remove();
      });

      isDev() &&
        console.log(
          `[NavigationManager] apo_style: forced ${elements.length} cards open (no collapse state in this mode)`
        );
    },

    /* HTML Generation */
    createTabButton(tabId, tabData, svgTemplates) {
      const button = document.createElement('button');
      button.className =
        'murphy-tab-button murphy-door-configurator__tabs__tab-button';
      button.setAttribute('data-tab', tabId);
      // P6: WAI-ARIA tab. Roving tabindex (active tab gets 0, others -1) and
      // aria-selected are maintained by the tab-activation code; new tabs start
      // unselected + out of the Tab sequence.
      button.setAttribute('type', 'button');
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', 'false');
      button.setAttribute('tabindex', '-1');
      button.innerHTML = this.generateTabHTML(tabData, svgTemplates);
      return button;
    },

    /**
     * Stable DOM ids for one category card's accordion wiring: the toggle
     * button, the panel it controls (.thumbnails) and the selection summary
     * that describes it. Category ids contain spaces and "&" ("Wood & Finish"),
     * so non-alphanumerics collapse to "-" (same convention as the tooltip
     * manager's tooltipDomId).
     * @param {string} categoryId
     * @returns {{toggle: string, panel: string, summary: string}}
     */
    _categoryDomIds(categoryId) {
      const base =
        CONSTANTS.CATEGORY_HEADER.ID_PREFIX +
        String(categoryId).replace(/[^a-zA-Z0-9_-]+/g, '-');
      return {
        toggle: base + '-toggle',
        panel: base + '-panel',
        summary: base + '-summary',
      };
    },

    /**
     * Mirror a category card's expanded state onto its accordion toggle
     * button -- the element assistive tech actually sees as the control. The
     * card itself is a plain container and must not carry aria-expanded (a
     * generic div ignores it, and it is what the card used to carry when it
     * was an invalid role="button" wrapper around other controls).
     *
     * @param {HTMLElement} categoryElement - the .category-button card
     * @param {boolean|null} expanded - true/false to set, null to REMOVE (the
     *   apo_style convention: cards there cannot collapse, so the attribute
     *   could only ever lie)
     * @param {Object} [dom] - optional uiUpdater.DOM adapter for the write
     */
    _setCategoryExpandedState(categoryElement, expanded, dom) {
      if (!categoryElement || typeof categoryElement.querySelector !== 'function')
        return;
      const toggle = categoryElement.querySelector(
        CONSTANTS.CATEGORY_HEADER.TOGGLE_SELECTOR
      );
      if (!toggle) return;
      if (expanded === null) {
        toggle.removeAttribute('aria-expanded');
        return;
      }
      const value = expanded ? 'true' : 'false';
      if (dom && typeof dom.setAttribute === 'function') {
        dom.setAttribute(toggle, 'aria-expanded', value);
      } else {
        toggle.setAttribute('aria-expanded', value);
      }
    },

    createCategoryElement(categoryId, categoryData) {
      const categoryElement = document.createElement('div');
      categoryElement.className = 'murphy-door-configurator__category-button';
      categoryElement.setAttribute('data-category', categoryId);
      // Initialize all category buttons as closed by default
      categoryElement.setAttribute('data-open', 'false');
      // WCAG 4.1.2 / axe nested-interactive (ADA remediation, 2026-09-28).
      // The CARD is a plain container, NOT a control. It used to carry
      // role="button" tabindex="0" aria-expanded itself, which made every
      // focusable thing inside it -- the "How to measure" / "More information"
      // info icons, the pass-through readout, every option tile, the slider --
      // a child of a button. Screen readers flatten a button's subtree, so
      // VoiceOver on iPhone either could not reach those controls or read the
      // whole card as one garbled button label.
      //
      // The accessible control is now the WAI-ARIA APG accordion shape built in
      // generateCategoryHTML(): a heading holding a native <button> with
      // aria-expanded + aria-controls, containing ONLY the category title. The
      // info icons are its siblings. Mouse behaviour is unchanged: the click
      // delegation in md-config-core-event-handler.js still keys on this card,
      // so a click anywhere on it (bar the info icons, which stop propagation)
      // toggles exactly as before, and the native button's own Enter/Space
      // click bubbles into that same path.
      //
      // 🚨 Do NOT put role/tabindex/aria-expanded back on this element.
      // Expanded state is mirrored onto the toggle via
      // _setCategoryExpandedState(); tests/assets/category-header-a11y.test.js
      // runs axe on the built card and fails on any nested interactive.
      // Path A: simplified allowlist was populated before buildCategories() ran
      if (
        simplifiedCategoryAllowlist !== null &&
        simplifiedCategoryAllowlist.has(categoryId)
      ) {
        categoryElement.setAttribute('data-simplified-visible', 'true');
      }
      categoryElement.innerHTML = this.generateCategoryHTML(
        categoryId,
        categoryData
      );

      // Publish category created event for other modules (like tooltip manager)
      this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CREATED, {
        categoryId,
        source: 'navigationManager.createCategoryElement',
        timestamp: Date.now(),
      });

      return categoryElement;
    },

    /**
     * The hollow-circle / check-circle status badge that sits beside a card or
     * tab title.
     *
     * ONE definition for what used to be two inline copies (the real category
     * header and the tab button) and one conspicuous absence (the gate card,
     * which rendered no badge at all -- so "Cabinets?" read visibly plainer
     * than every real card beside it).
     *
     * Returns '' when the shared hidden SVG templates are missing. The old
     * inline copies dereferenced `.outerHTML` unguarded and would have thrown;
     * degrading to no badge is strictly safer and matches how the gate builder
     * already guarded its other shared SVGs.
     *
     * @param {Object} svgTemplates - from getSvgTemplates()
     * @returns {string}
     */
    buildStatusCircleHTML(svgTemplates) {
      if (
        !svgTemplates ||
        !svgTemplates.openCircle ||
        !svgTemplates.circleCheck
      ) {
        return '';
      }
      return `<span class="murphy-door-configurator__status-circle open-circle">
          <span class="open-circle">${svgTemplates.openCircle.outerHTML}</span>
          <span class="circle-check">${svgTemplates.circleCheck.outerHTML}</span>
        </span>`;
    },

    /**
     * Creates a synthetic "gate card" element (e.g. "Cabinets?") from a
     * GATE_CARD_DEFINITIONS entry. Shares the real category button's outer
     * class so it rides the existing showCategoriesForTab() sweep for free
     * once pushed into _tabToCategoryElementsMap (see buildCategories()) --
     * but carries `data-category-gate`, NEVER `data-category`, since
     * `[data-category]` is what several existing sweeps (ATC-completion
     * validation, progress tracking) key off as "real, required category."
     * A gate card must never be mistaken for one.
     * @param {Object} gateDef - an entry from CONSTANTS.GATE_CARD_DEFINITIONS
     * @returns {HTMLElement}
     */
    createGateCardElement(gateDef) {
      const gateElement = document.createElement('div');
      gateElement.className =
        'murphy-door-configurator__category-button murphy-door-configurator__category-button--gate';
      gateElement.setAttribute('data-category-gate', gateDef.id);
      // Mirrors a real category's completed/selected-closed state (blue
      // background, white text, options collapsed) once answered -- see
      // md-config-ui-gate-visibility-manager.js's applyGateAnsweredState().
      gateElement.setAttribute('data-answered', 'false');
      // Mirrors a real category's data-open (createCategoryElement above):
      // collapsed by default until auto-navigation makes this gate the
      // current step (UI_GATE_OPEN_REQUEST handler below sets this true/
      // false). Without it every gate card's options render fully expanded
      // from page load regardless of progress -- confirmed live 2026-08-13.
      gateElement.setAttribute('data-open', 'false');

      // Gate options are label-only. A per-option product image used to be
      // emitted here for gates in a GATE_COMBINATION_RESOLUTIONS group
      // (Swing/Hinge -> Door Handing), with the src filled in later by
      // md-config-ui-gate-visibility-manager.js's applyGateOptionImages().
      // Both were removed with those gates on 2026-08-27. Do NOT re-add the
      // markup alone: this pass is build-time only and cannot resolve a
      // variant image, so the <img> shipped with no src and depended entirely
      // on that now-deleted resolver.

      // Gate options reuse the REAL option tile's selected treatment rather
      // than styling of their own (Daniel 2026-08-17: "we want the same look
      // for every card we shouldnt divert styles"). That means the same
      // status-icon markup a real thumbnail emits
      // (md-config-core-data-pipeline.js) -- same classes, same SVGs from the
      // same shared hidden templates -- so section-md-configurator.css styles
      // it identically with no parallel rules. The glow itself is applied in
      // section-md-apo-mode.css off the existing --active-glow-shadow token
      // and glowPulse keyframes, again shared rather than redefined.
      //
      // NOTE: .thumbnail-save-button has no click handler anywhere in the
      // codebase -- it is decorative on real tiles too (its only other
      // reference is an exclusion in the back-navigation branch of
      // md-config-core-event-handler.js). It is replicated here deliberately,
      // for visual parity, on Daniel's explicit call. If the dead button is
      // ever wired up or removed, this markup has to move with it.
      //
      // It is a <span>, never a <button>, and that is load-bearing: a gate
      // option IS a <button>, and nested buttons are invalid HTML -- the
      // parser auto-closes the outer one the moment it meets the inner one.
      // Rendering it as a button silently swallowed the option's label span
      // and left an empty badge behind (caught live 2026-08-17). The CSS keys
      // on the .thumbnail-save-button CLASS, not the tag, so the span styles
      // identically -- and since the control does nothing, a span is the more
      // honest element anyway. aria-hidden keeps the decorative badge out of
      // the accessible name of the real button wrapping it.
      //
      // 2026-08-20: this used to note that real tiles shipped a <button> here.
      // They no longer do. Once the P7 work made every option tile
      // role="button", that <button> became nested-interactive, so all four
      // builders (this one, _generateThumbnailHTML, and both status-icon
      // builders in md-config-core-data-pipeline.js) now emit the same
      // aria-hidden span. The gate path was the pattern the others copied.
      const gateSvgTemplates = this.getSvgTemplates();
      const gateSaveSvg = gateSvgTemplates.saveIcon
        ? gateSvgTemplates.saveIcon.outerHTML
        : '';
      const gateCheckSvg = gateSvgTemplates.circleCheck
        ? gateSvgTemplates.circleCheck.outerHTML
        : '';
      const gateStatusIconHTML =
        gateSaveSvg || gateCheckSvg
          ? `<span class="murphy-door-configurator__thumbnail-status-icon" aria-hidden="true">
              <span class="thumbnail-save-button">${gateSaveSvg}</span>
              <span class="thumbnail-circle-check">${gateCheckSvg}</span>
            </span>`
          : '';

      const optionsHTML = gateDef.options
        .map(
          (option) => `
            <button
              type="button"
              class="murphy-door-configurator__gate-card-option"
              data-action="set-gate-answer"
              data-gate-id="${escapeHTML(gateDef.id)}"
              data-answer="${escapeHTML(option.value)}"
              aria-pressed="false"
            >
              <span class="murphy-door-configurator__gate-card-option-label">${escapeHTML(option.label)}</span>
              ${gateStatusIconHTML}
            </button>
          `
        )
        .join('');

      // Reuses the exact same pencil-icon markup + SVG source real categories
      // use (getSvgTemplates() reads the shared hidden SVG templates already
      // in the DOM) -- the existing CSS shows it on hover of ANY
      // .category-button, gate or real, so no new CSS is needed for it to
      // appear on the collapsed/answered gate card too.
      const svgTemplates = this.getSvgTemplates();
      const pencilIconHTML = svgTemplates.pencilEdit
        ? `
        <span class="murphy-door-configurator__pencil-edit-icon" data-gate-id="${escapeHTML(gateDef.id)}">
          <span class="murphy-door-configurator__pencil-edit-icon__pencil">${svgTemplates.pencilEdit.outerHTML}</span>
        </span>
      `
        : '';

      // XSS-SAFE: gateDef comes from CONSTANTS.GATE_CARD_DEFINITIONS (a frozen,
      // hardcoded constant, never user/merchant input) and optionsHTML is
      // built entirely from escapeHTML()'d values above; sanitizeHTML wrapper
      // kept for audit-trail consistency with the rest of this file (Pattern #17).
      // Reuses the real category's .category-title / .category-user-selection
      // classes (not just the gate-specific ones) so the completed/blue state
      // in section-md-configurator.css is inherited for free -- no separate
      // "match the other cards" styling to keep in sync.
      // Same badge markup real category headers and tab buttons emit, from the
      // one shared builder -- so a gate card is no longer the only card in the
      // column with a bare title. State is driven by
      // md-config-ui-gate-visibility-manager.js, which calls the same
      // utils.setStatusCircleComplete() StatusManager uses for real cards.
      const gateStatusCircleHTML = this.buildStatusCircleHTML(gateSvgTemplates);

      gateElement.innerHTML = sanitizeHTML(`
        <div class="murphy-door-configurator__category-title-wrapper">
          <div class="murphy-door-configurator__title-selection-group">
            <span class="murphy-door-configurator__category-title murphy-door-configurator__gate-card-label" id="gate-label-${escapeHTML(gateDef.id)}">${escapeHTML(gateDef.labelHTML)}${gateStatusCircleHTML}</span>
          </div>
          <span class="murphy-door-configurator__category-user-selection murphy-door-configurator__gate-card-answer" data-gate-answer-summary></span>
        </div>
        ${pencilIconHTML}
        <div class="murphy-door-configurator__gate-card-options" role="group" aria-labelledby="gate-label-${escapeHTML(gateDef.id)}">${optionsHTML}</div>
      `);

      return gateElement;
    },

    generateTabHTML(tabData, svgTemplates) {
      return `
        ${escapeHTML(tabData.name)}${this.buildStatusCircleHTML(svgTemplates)}
      `;
    },

    generateCategoryHTML(categoryId, categoryData) {
      const svgTemplates = this.getSvgTemplates();

      // Check if tooltip is available for this category
      let hasTooltip = false;

      if (
        window.MurphyDoorConfigurator.TooltipManager &&
        typeof window.MurphyDoorConfigurator.TooltipManager
          .hasTooltipContent === 'function'
      ) {
        hasTooltip =
          window.MurphyDoorConfigurator.TooltipManager.hasTooltipContent(
            categoryId
          );
      }

      // Generate tooltip icon HTML if tooltip content exists
      let tooltipIconHTML = '';

      // Use normalized comparison for robust category matching
      const normalizedCategoryId = categoryId
        ? categoryId.toLowerCase().replace(/[\s-]/g, '')
        : '';

      // Measurement categories get tape measure SVG + "how to measure" text
      const isMeasurementCategory =
        MEASUREMENT_CATEGORIES.has(normalizedCategoryId);

      if (hasTooltip && svgTemplates) {
        if (isMeasurementCategory && svgTemplates.measuringTape) {
          // Measurement categories: tape measure SVG + "how to measure" text.
          // HOVER still opens the tooltip; ACTIVATION (click/Enter/Space) is
          // handed to the global "Will it fit?" wizard by the tooltip manager
          // -- hence aria-haspopup='dialog' + aria-controls, matching the FAB
          // and the PDP "Will it fit?" button. See handOffToWillItFit in
          // md-config-ui-tooltip-manager.js.
          tooltipIconHTML = `<span class="murphy-door-info-icon murphy-door-info-icon--measurement" data-category="${escapeHTML(categoryId)}" aria-label="How to measure" role="button" tabindex="0" aria-haspopup="dialog" aria-controls="willitfit-modal">
             ${svgTemplates.measuringTape.outerHTML}
             <span class="measurement-help-text">how to measure</span>
           </span>`;
        } else if (svgTemplates.infoIcon) {
          // Non-measurement categories: standard info icon
          tooltipIconHTML = `<span class="murphy-door-info-icon" data-category="${escapeHTML(categoryId)}" aria-label="More information" role="button" tabindex="0">
             ${svgTemplates.infoIcon.outerHTML}
           </span>`;
        }
      }

      // Header pictogram slot (Door Depth, Shelf Style, ... see
      // CONSTANTS.CATEGORY_PICTOGRAMS). An EMPTY, aria-hidden, hidden span;
      // md-config-ui-category-pictograms.js draws into it and unhides it.
      // Decorative only: the slider's tooltip / aria-valuetext and the card's
      // own description carry the facts.
      //
      // This slot replaced the Door Depth header's pass-through readout
      // (doorframe icon + "PTS", role=button tooltip trigger). The pass-through
      // figure and its explainer tooltip are still reachable from the price
      // bar / summary trust bar (snippets/pass-through-space-info-display.liquid,
      // category 'pass-through-space') and the mobile gallery PTS badge
      // (sections/md-configurator.liquid .murphy-door-configurator__pts-badge),
      // and the Door Depth card's mobile metrics dashboard now carries the
      // out-swing "Walk-through when open" row.
      const pictogramKind =
        CONSTANTS.CATEGORY_PICTOGRAMS &&
        Object.prototype.hasOwnProperty.call(
          CONSTANTS.CATEGORY_PICTOGRAMS,
          categoryId
        )
          ? CONSTANTS.CATEGORY_PICTOGRAMS[categoryId]
          : null;
      const pictogramSlotHTML = pictogramKind
        ? `<span class="murphy-door-configurator__category-pictogram" data-pictogram-category="${escapeHTML(categoryId)}" data-pictogram-kind="${escapeHTML(pictogramKind)}" aria-hidden="true" hidden></span>`
        : '';

      // WAI-ARIA APG accordion header (WCAG 4.1.2, axe nested-interactive).
      // heading > native <button aria-expanded aria-controls> holding ONLY the
      // title. The info icon and the decorative status circle stay in the same
      // flex row, in the same order, as SIBLINGS of the heading, so the layout
      // is unchanged and neither is nested inside the button. The selection
      // summary cannot move inside the button without also pulling the info
      // icons in (they share the row), so it is wired as the button's
      // accessible DESCRIPTION instead: VoiceOver reads "Wood & Finish,
      // collapsed, button, Red Oak Veneer | Warm Walnut $1,190".
      // role="heading" on a span rather than a real <hN>: the theme's base
      // heading styles would restyle the title; the ARIA role carries the same
      // semantics with zero visual change. Level from CONSTANTS.CATEGORY_HEADER
      // (2 -- the configurator's only other heading is the product <h1>).
      const header = CONSTANTS.CATEGORY_HEADER;
      const domIds = this._categoryDomIds(categoryId);

      return `
    <div class="murphy-door-configurator__category-title-wrapper">
      <div class="murphy-door-configurator__title-selection-group">
        <span class="murphy-door-configurator__category-title">
          <span class="${header.HEADING_CLASS}" role="heading" aria-level="${header.HEADING_LEVEL}"><button type="button" class="${header.TOGGLE_CLASS}" id="${domIds.toggle}" aria-expanded="false" aria-controls="${domIds.panel}" aria-describedby="${domIds.summary}">${escapeHTML(categoryId)}</button></span>${tooltipIconHTML}${this.buildStatusCircleHTML(svgTemplates)}
        </span>
        ${pictogramSlotHTML}
      </div>
      <span class="murphy-door-configurator__category-user-selection" id="${domIds.summary}"></span>
    </div>
    <span class="murphy-door-configurator__pencil-edit-icon" data-category-id="${escapeHTML(categoryId)}">
      <span class="murphy-door-configurator__pencil-edit-icon__pencil">${svgTemplates.pencilEdit.outerHTML}</span>
      <span class="murphy-door-configurator__pencil-edit-icon__close">${svgTemplates.closeIcon ? svgTemplates.closeIcon.outerHTML : ''}</span>
    </span>
    <div class="murphy-door-configurator__thumbnails" id="${domIds.panel}" role="group" aria-label="${escapeHTML(categoryId)} options">
      ${(() => {
        // 🚀 PERFORMANCE FIX (P0): Always use lightweight placeholder during initial category build.
        // Thumbnail HTML (with <img> tags) is hydrated on-demand when the tab becomes visible
        // via showCategoriesForTab(). This prevents 1,000+ images from being injected into the
        // DOM at page load — the root cause of the Murphy Bed 1.2 GB image loading bug.
        // Previous behavior: eagerly generated all thumbnail HTML for ALL categories, dumping
        // every <img> into the DOM simultaneously even for hidden categories.
        return `<div class="murphy-door-configurator__loading-placeholder" data-category="${escapeHTML(categoryId)}">Loading thumbnails...</div>`;
      })()}
    </div>
    ${(() => {
      // SLIDER-ONLY: Apply button rendered AFTER the slider/thumbnails container.
      // Must remain a sibling (not child) of thumbnails because renderSlider() calls
      // container.innerHTML which would destroy any child injected inside.
      // Thumbnail-based categories use double-click-to-apply UX instead.
      // CCP delegation: conductor.shouldUseCategorySlider() checks PPD slider metadata.
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (
        conductor &&
        typeof conductor.shouldUseCategorySlider === 'function' &&
        conductor.shouldUseCategorySlider(categoryId)
      ) {
        return `<div class="murphy-door-configurator__apply-button-wrapper">
          <button class="murphy-door-configurator__apply-button" style="display: none;">${svgTemplates.saveIcon?.outerHTML || 'Apply'}</button>
        </div>`;
      }
      return '';
    })()}
  `;
    },

    /**
     * PHASE 2.H.1: Product Virtualization Layer
     *
     * Generates HTML for thumbnails using virtualized products (Product | CompositeProduct)
     * This method supports the new Product Virtualization Layer by handling both:
     * - Regular Product objects (individual products)
     * - CompositeProduct objects (grouped variants)
     *
     * INTEGRATION: Called by ui-updater.js when smart sorting provides virtualized products
     *
     * @param {string} categoryId - Category identifier
     * @param {Array} virtualizedProducts - Array of Product | CompositeProduct objects
     * @returns {string} Generated HTML for virtualized product thumbnails
     */
    generateThumbnailsHTMLWithProducts(categoryId, virtualizedProducts) {
      // PATTERN 6: Bootstrap Runtime Protection - queue if PPD not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            '[NavigationManager] generateThumbnailsHTMLWithProducts queued - bootstrap not complete'
          );
        pendingCalls.push({
          fn: this.generateThumbnailsHTMLWithProducts,
          context: this,
          args: [categoryId, virtualizedProducts],
        });
        return '';
      }

      const startTime = window.performance
        ? window.performance.now()
        : Date.now();
      const METHOD_SIGNATURE =
        'navigationManager.generateThumbnailsHTMLWithProducts';

      try {
        // 1. Publish generation started
        this.publishEvent(CONSTANTS.EVENTS.UI_THUMBNAILS_GENERATION_STARTED, {
          categoryId,
          productCount: virtualizedProducts ? virtualizedProducts.length : 0,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        if (
          !virtualizedProducts ||
          !Array.isArray(virtualizedProducts) ||
          virtualizedProducts.length === 0
        ) {
          const errorMsg = `NO PRODUCTS ERROR: No virtualized products provided`;
          isDev() &&
            console.error(
              'NavigationManager.generateThumbnailsHTMLWithProducts:',
              errorMsg
            );

          // Publish failed event
          this.publishEvent(CONSTANTS.EVENTS.UI_THUMBNAILS_GENERATION_FAILED, {
            error: errorMsg,
            categoryId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });

          // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
          return `<div class="murphy-door-configurator__error-thumbnail">
            ${escapeHTML(errorMsg)}
          </div>`;
        }

        const thumbnailsHTML = virtualizedProducts
          .map((product, index) => {
            try {
              let html;
              if (product.isComposite) {
                html = this.generateCompositeProductThumbnailHTML(
                  product,
                  categoryId,
                  index
                );
              } else {
                html = this.generateIndividualProductThumbnailHTML(
                  product,
                  categoryId,
                  index
                );
              }

              // 2. Publish per-thumbnail milestone
              this.publishEvent(CONSTANTS.EVENTS.UI_THUMBNAIL_GENERATED, {
                categoryId,
                productId: product.id,
                isComposite: product.isComposite,
                index,
                timestamp: Date.now(),
                source: METHOD_SIGNATURE,
              });

              return html;
            } catch (error) {
              const errorMsg = `INDIVIDUAL THUMBNAIL ERROR: ${error.message}`;
              isDev() &&
                console.error(
                  'NavigationManager.generateThumbnailsHTMLWithProducts (individual):',
                  errorMsg
                );

              // Publish per-thumbnail failure
              this.publishEvent(
                CONSTANTS.EVENTS.UI_THUMBNAILS_GENERATION_FAILED,
                {
                  error: errorMsg,
                  categoryId,
                  productId: product.id,
                  index,
                  timestamp: Date.now(),
                  source: METHOD_SIGNATURE,
                }
              );

              // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
              return `<div class="murphy-door-configurator__error-thumbnail">
                ${escapeHTML(errorMsg)}
              </div>`;
            }
          })
          .filter((html) => html.length > 0)
          .join('');

        // 3. Track performance
        const endTime = window.performance
          ? window.performance.now()
          : Date.now();
        const executionTime = endTime - startTime;
        if (executionTime > PERFORMANCE_CONSTANTS.THRESHOLD_MS) {
          this.publishEvent(
            CONSTANTS.EVENTS.PERFORMANCE_THUMBNAIL_GENERATION_SLOW,
            {
              executionTime: executionTime.toFixed(2),
              threshold: PERFORMANCE_CONSTANTS.THRESHOLD_MS,
              operation: 'generateThumbnailsHTMLWithProducts',
              categoryId,
              productCount: virtualizedProducts.length,
              source: METHOD_SIGNATURE,
              timestamp: Date.now(),
            }
          );

          isDev() &&
            console.warn(
              `⚠️ PERFORMANCE: Thumbnail generation took ${executionTime.toFixed(2)}ms ` +
                `(threshold: ${PERFORMANCE_CONSTANTS.THRESHOLD_MS}ms)`
            );
        }

        // 4. Publish generation completed
        this.publishEvent(CONSTANTS.EVENTS.UI_THUMBNAILS_GENERATION_COMPLETED, {
          categoryId,
          productCount: virtualizedProducts.length,
          executionTime: executionTime.toFixed(2),
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return thumbnailsHTML;
      } catch (error) {
        const errorMsg = `THUMBNAILS GENERATION ERROR: ${error.message}`;
        isDev() &&
          console.error(
            'NavigationManager.generateThumbnailsHTMLWithProducts:',
            errorMsg
          );

        // Publish operation failed
        this.publishEvent(CONSTANTS.EVENTS.UI_THUMBNAILS_GENERATION_FAILED, {
          error: errorMsg,
          stack: error.stack,
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
        return `<div class="murphy-door-configurator__error-thumbnail">
          ${escapeHTML(errorMsg)}
        </div>`;
      }
    },

    /**
     * Generates HTML for a CompositeProduct thumbnail
     * CompositeProducts represent grouped variants as single selectable entities
     *
     * @param {Object} compositeProduct - CompositeProduct object with variants
     * @param {string} categoryId - Category identifier
     * @param {number} index - Index for unique IDs
     * @returns {string} Generated HTML for CompositeProduct thumbnail
     */
    generateCompositeProductThumbnailHTML(compositeProduct, categoryId, index) {
      try {
        // 🔥 ARCHITECTURAL ENFORCEMENT: Detect direct external calls (ZERO backwards compatibility)
        const stack = new Error().stack;
        const isInternalCall =
          stack.includes('navigationManager') ||
          stack.includes('handleCompositeThumbnailRequest') ||
          stack.includes('generateThumbnailsHTMLWithProducts') ||
          stack.includes('core-event-bus'); // Allow event-driven calls through CoreEventBus

        if (!isInternalCall) {
          throw new Error(
            'CRITICAL CCP VIOLATION: Direct call to generateCompositeProductThumbnailHTML() is PROHIBITED. ' +
              'Use event-driven pattern: publish ui:composite_thumbnail_generation_requested. ' +
              'This is a CCP architectural violation - all module-to-module communication must use CoreEventBus. ' +
              'Stack trace: ' +
              stack
          );
        }

        // 🔥 SURGICAL REFACTOR: Use shared template while preserving CompositeProduct behavior
        return this._generateThumbnailHTML({
          type: 'composite',
          data: compositeProduct,
          categoryId,
          index,
        });
      } catch (error) {
        const errorMsg = `COMPOSITE THUMBNAIL ERROR: ${error.message}`;
        isDev() &&
          console.error(
            'NavigationManager.generateCompositeProductThumbnailHTML:',
            errorMsg
          );
        // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
        return `<div class="murphy-door-configurator__error-thumbnail">
          ${escapeHTML(errorMsg)}
        </div>`;
      }
    },

    /**
     * Generates HTML for an individual Product thumbnail
     * Maintains compatibility with existing individual product display
     *
     * @param {Object} product - Regular product object
     * @param {string} categoryId - Category identifier
     * @param {number} index - Index for unique IDs
     * @returns {string} Generated HTML for individual product thumbnail
     */
    generateIndividualProductThumbnailHTML(product, categoryId, index) {
      try {
        // 🔥 SURGICAL REFACTOR: Use shared template while preserving Individual Product behavior
        return this._generateThumbnailHTML({
          type: 'product',
          data: product,
          categoryId,
          index,
        });
      } catch (error) {
        const errorMsg = `PRODUCT THUMBNAIL ERROR: ${error.message}`;
        isDev() &&
          console.error(
            'NavigationManager.generateIndividualProductThumbnailHTML:',
            errorMsg
          );
        // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
        return `<div class="murphy-door-configurator__error-thumbnail">
          ${escapeHTML(errorMsg)}
        </div>`;
      }
    },

    /**
     * Generates HTML for a variant thumbnail.
     *
     * This method follows the specialized responsibility of the Navigation Manager
     * in the Central Conductor Pattern by:
     * 1. Handling only HTML generation concerns
     * 2. Not accessing business logic or state directly
     * 3. Receiving formatted data through parameters rather than retrieving it itself
     * 4. Focusing on UI structure and accessibility compliance
     *
     * @param {Object} variantData - Data for the variant to render
     * @param {string} variantData.id - Unique identifier for the variant
     * @param {Object} [variantData.image] - Image data for the variant
     * @param {string} [variantData.image.src] - Source URL for the variant image
     * @param {string} [variantData.image.alt] - Alt text for the variant image
     * @param {string} displayTitle - Formatted title with price to display
     * @returns {string} Generated HTML for the variant thumbnail
     */
    generateVariantThumbnailHTML(variantData, displayTitle) {
      try {
        if (!variantData || !variantData.id) {
          const errorMsg = `VARIANT THUMBNAIL ERROR: Invalid variant data`;
          isDev() &&
            console.error(
              'NavigationManager.generateVariantThumbnailHTML:',
              errorMsg
            );
          // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
          return `<div class="murphy-door-configurator__error-thumbnail">
            ${escapeHTML(errorMsg)}
          </div>`;
        }

        // 🔥 SURGICAL REFACTOR: Use shared template while preserving Variant behavior
        return this._generateThumbnailHTML({
          type: 'variant',
          data: variantData,
          categoryId: null, // Variants don't need category-specific fallbacks
          index: 0, // Variants typically don't need index
          displayOverrides: {
            displayTitle: displayTitle,
          },
        });
      } catch (error) {
        const errorMsg = `VARIANT THUMBNAIL ERROR: ${error.message}`;
        isDev() &&
          console.error(
            'NavigationManager.generateVariantThumbnailHTML error:',
            errorMsg
          );
        // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
        return `<div class="murphy-door-configurator__error-thumbnail">
          ${escapeHTML(errorMsg)}
        </div>`;
      }
    },

    /* Navigation State Methods */
    /**
     * Activates a tab by its ID with comprehensive error handling
     * @param {string} tabId - The ID of the tab to activate
     * @returns {boolean} True if activation succeeded, false otherwise
     * @fires ui:tab_activated
     * @fires ui:tab_activation_failed
     * @throws {Error} Validation errors for invalid tab IDs
     */
    activateTab(tabId) {
      // PATTERN 6: Bootstrap Runtime Protection - queue if PPD not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            `[NavigationManager] activateTab("${tabId}") queued - bootstrap not complete`
          );
        pendingCalls.push({
          fn: this.activateTab,
          context: this,
          args: [tabId],
        });
        return false;
      }

      const METHOD_SIGNATURE = 'navigationManager.activateTab';

      try {
        // Validation: Check tabId parameter
        if (!tabId || typeof tabId !== 'string') {
          const errorMsg = `Invalid tab ID provided: ${tabId}`;
          this.publishEvent(CONSTANTS.EVENTS.UI_TAB_ACTIVATION_FAILED, {
            error: errorMsg,
            tabId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() && console.error(`❌ NAVIGATION MANAGER: ${errorMsg}`);
          return false;
        }

        const DOM = getDOM();
        if (!DOM) {
          const errorMsg = ERROR_MESSAGES.DOM_NOT_AVAILABLE;
          this.publishEvent(CONSTANTS.EVENTS.UI_TAB_ACTIVATION_FAILED, {
            error: errorMsg,
            tabId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() &&
            console.warn(
              `🎯 NAVIGATION MANAGER: ${errorMsg} - cannot activate tab: ${tabId}`
            );
          return false;
        }

        const tab = DOM.getTabElement(tabId);
        if (!tab) {
          const errorMsg = `Tab element not found for ID: ${tabId}`;
          this.publishEvent(CONSTANTS.EVENTS.UI_TAB_ACTIVATION_FAILED, {
            error: errorMsg,
            tabId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() && console.error(`❌ NAVIGATION MANAGER: ${errorMsg}`);
          return false;
        }

        // Perform activation - UIUpdater handles deactivation via UI_TAB_ACTIVATED event subscription
        DOM.addClass(tab, 'active');
        DOM.addClass(tab, 'murphy-tab-button--active');
        DOM.addClass(tab, 'murphy-door-configurator__tabs__tab-button--active');
        // P6: reflect the active tab to assistive tech + pull it into the Tab
        // sequence (roving tabindex — only the active tab is Tab-reachable).
        DOM.setAttribute(tab, 'aria-selected', 'true');
        DOM.setAttribute(tab, 'tabindex', '0');
        this.showCategoriesForTab(tabId);

        // CCP COMPLIANT: Publish tab readiness state after activation
        this.publishTabReadinessState();

        // Publish success event
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_ACTIVATED, {
          tabId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return true;
      } catch (error) {
        // Publish failure event with stack trace
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_ACTIVATION_FAILED, {
          error: error.message || error,
          stack: error.stack,
          tabId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
        isDev() &&
          console.error(
            `❌ NAVIGATION MANAGER: Tab activation failed - ${error.message}`,
            error.stack
          );
        return false;
      }
    },

    /**
     * Deactivates a tab by its ID with comprehensive error handling
     * @param {string} tabId - The ID of the tab to deactivate
     * @returns {boolean} True if deactivation succeeded, false otherwise
     * @fires ui:tab_deactivated
     * @fires ui:tab_deactivation_failed
     * @throws {Error} Validation errors for invalid tab IDs
     */
    deactivateTab(tabId) {
      const METHOD_SIGNATURE = 'navigationManager.deactivateTab';

      try {
        // Validation: Check tabId parameter
        if (!tabId || typeof tabId !== 'string') {
          const errorMsg = `Invalid tab ID provided: ${tabId}`;
          this.publishEvent(CONSTANTS.EVENTS.UI_TAB_DEACTIVATION_FAILED, {
            error: errorMsg,
            tabId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() && console.error(`❌ NAVIGATION MANAGER: ${errorMsg}`);
          return false;
        }

        const DOM = getDOM();
        if (!DOM) {
          const errorMsg = ERROR_MESSAGES.DOM_NOT_AVAILABLE;
          this.publishEvent(CONSTANTS.EVENTS.UI_TAB_DEACTIVATION_FAILED, {
            error: errorMsg,
            tabId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() &&
            console.warn(
              `🎯 NAVIGATION MANAGER: ${errorMsg} - cannot deactivate tab: ${tabId}`
            );
          return false;
        }

        const tab = DOM.getTabElement(tabId);
        if (!tab) {
          const errorMsg = `Tab element not found for ID: ${tabId}`;
          this.publishEvent(CONSTANTS.EVENTS.UI_TAB_DEACTIVATION_FAILED, {
            error: errorMsg,
            tabId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() && console.error(`❌ NAVIGATION MANAGER: ${errorMsg}`);
          return false;
        }

        // Perform deactivation
        DOM.removeClass(tab, 'active');
        DOM.removeClass(tab, 'murphy-tab-button--active');
        DOM.removeClass(
          tab,
          'murphy-door-configurator__tabs__tab-button--active'
        );
        // P6: mark unselected + drop out of the Tab sequence (roving tabindex).
        DOM.setAttribute(tab, 'aria-selected', 'false');
        DOM.setAttribute(tab, 'tabindex', '-1');

        // Publish success event
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_DEACTIVATED, {
          tabId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return true;
      } catch (error) {
        // Publish failure event with stack trace
        this.publishEvent(CONSTANTS.EVENTS.UI_TAB_DEACTIVATION_FAILED, {
          error: error.message || error,
          stack: error.stack,
          tabId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
        isDev() &&
          console.error(
            `❌ NAVIGATION MANAGER: Tab deactivation failed - ${error.message}`,
            error.stack
          );
        return false;
      }
    },

    /**
     * 🎯 CCP COMPLIANT: Activates the first available tab
     * Used as fallback by URL Recovery Animator via event-driven requests
     */
    activateFirstAvailableTab() {
      try {
        // Look for any tab with data-tab attribute
        const firstTab = document.querySelector('[data-tab]');
        if (firstTab && firstTab.hasAttribute('data-tab')) {
          const tabId = firstTab.getAttribute('data-tab');
          isDev() &&
            console.log(
              '🎯 NAVIGATION MANAGER: Found first available tab:',
              tabId
            );
          this.activateTab(tabId);
          return true;
        } else {
          isDev() &&
            console.warn(
              '🎯 NAVIGATION MANAGER: No tabs found with data-tab attribute'
            );
          return false;
        }
      } catch (error) {
        isDev() &&
          console.error(
            '🎯 NAVIGATION MANAGER: Error activating first tab:',
            error
          );
        return false;
      }
    },

    /**
     * Expands a category by its ID with comprehensive error handling
     * @param {string} categoryId - The ID of the category to expand
     * @returns {boolean} True if expansion succeeded, false otherwise
     * @fires ui:category_expansion_started
     * @fires ui:category_expanded
     * @fires ui:category_expansion_failed
     * @throws {Error} Validation errors for invalid category IDs
     */
    expandCategory(categoryId) {
      // PATTERN 6: Bootstrap Runtime Protection - queue if PPD not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            `[NavigationManager] expandCategory("${categoryId}") queued - bootstrap not complete`
          );
        pendingCalls.push({
          fn: this.expandCategory,
          context: this,
          args: [categoryId],
        });
        return false;
      }

      const METHOD_SIGNATURE = 'navigationManager.expandCategory';

      try {
        // Publish expansion started
        this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_EXPANSION_STARTED, {
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // Validation: Check categoryId parameter
        if (!categoryId || typeof categoryId !== 'string') {
          const errorMsg = `Invalid category ID provided: ${categoryId}`;
          this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_EXPANSION_FAILED, {
            error: errorMsg,
            categoryId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() && console.error(`❌ NAVIGATION MANAGER: ${errorMsg}`);
          return false;
        }

        const DOM = getDOM();
        if (!DOM) {
          const errorMsg = ERROR_MESSAGES.DOM_NOT_AVAILABLE;
          this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_EXPANSION_FAILED, {
            error: errorMsg,
            categoryId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() &&
            console.warn(
              `🎯 NAVIGATION MANAGER: ${errorMsg} - cannot expand category: ${categoryId}`
            );
          return false;
        }

        const category = DOM.getCategoryElement(categoryId);
        if (!category) {
          const errorMsg = `Category element not found for ID: ${categoryId}`;
          this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_EXPANSION_FAILED, {
            error: errorMsg,
            categoryId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() && console.error(`❌ NAVIGATION MANAGER: ${errorMsg}`);
          return false;
        }

        // Perform expansion - add visual classes for border and pencil icon
        DOM.setAttribute(category, 'data-open', 'true');
        // P6: mirror the expanded state to assistive tech -- on the header's
        // native toggle button, not the card (WCAG 4.1.2 nested-interactive).
        this._setCategoryExpandedState(category, true, DOM);
        DOM.addClass(
          category,
          'murphy-door-configurator__category-button--open'
        );
        const thumbnails = DOM.getThumbnailsContainer(categoryId);
        if (thumbnails) {
          DOM.addClass(
            thumbnails,
            'murphy-door-configurator__thumbnails--open'
          );
        }

        // Publish success event
        this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_EXPANDED, {
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return true;
      } catch (error) {
        // Publish failure event with stack trace
        this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_EXPANSION_FAILED, {
          error: error.message || error,
          stack: error.stack,
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
        isDev() &&
          console.error(
            `❌ NAVIGATION MANAGER: Category expansion failed - ${error.message}`,
            error.stack
          );
        return false;
      }
    },

    /**
     * Collapses a category by its ID with comprehensive error handling
     * @param {string} categoryId - The ID of the category to collapse
     * @returns {boolean} True if collapse succeeded, false otherwise
     * @fires ui:category_collapsed
     * @fires ui:category_collapse_failed
     * @throws {Error} Validation errors for invalid category IDs
     */
    collapseCategory(categoryId) {
      const METHOD_SIGNATURE = 'navigationManager.collapseCategory';

      try {
        // Validation: Check categoryId parameter
        if (!categoryId || typeof categoryId !== 'string') {
          const errorMsg = `Invalid category ID provided: ${categoryId}`;
          this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_COLLAPSE_FAILED, {
            error: errorMsg,
            categoryId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() && console.error(`❌ NAVIGATION MANAGER: ${errorMsg}`);
          return false;
        }

        // apo_style has no collapsed state -- every card stays expanded for
        // the whole session (Ryan review 2026-08-17). Report success rather
        // than failure: callers treat `false` as an error worth publishing a
        // COLLAPSE_FAILED event for, and "we intentionally do not collapse
        // here" is not a failure.
        //
        // Deliberately placed AFTER the categoryId validation above, not
        // before it. An earlier version returned first, which made that guard
        // dead code for this entire experience -- collapseCategory(undefined)
        // would have reported success. A guard that cannot fire is not safety.
        const _apoConductor = window.MurphyDoorConfigurator.conductor;
        if (_apoConductor?._getConfiguratorExperience?.() === 'apo_style') {
          return true;
        }

        const DOM = getDOM();
        if (!DOM) {
          const errorMsg = ERROR_MESSAGES.DOM_NOT_AVAILABLE;
          this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_COLLAPSE_FAILED, {
            error: errorMsg,
            categoryId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() &&
            console.warn(
              `🎯 NAVIGATION MANAGER: ${errorMsg} - cannot collapse category: ${categoryId}`
            );
          return false;
        }

        const category = DOM.getCategoryElement(categoryId);
        if (!category) {
          const errorMsg = `Category element not found for ID: ${categoryId}`;
          this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_COLLAPSE_FAILED, {
            error: errorMsg,
            categoryId,
            timestamp: Date.now(),
            source: METHOD_SIGNATURE,
          });
          isDev() && console.error(`❌ NAVIGATION MANAGER: ${errorMsg}`);
          return false;
        }

        // Perform collapse - remove visual classes for border and pencil icon
        DOM.setAttribute(category, 'data-open', 'false');
        // P6: mirror the collapsed state to assistive tech -- on the header's
        // native toggle button, not the card (WCAG 4.1.2 nested-interactive).
        this._setCategoryExpandedState(category, false, DOM);
        DOM.removeClass(
          category,
          'murphy-door-configurator__category-button--open'
        );

        // Restore completed-state badge if this category has a confirmed selection.
        // CATEGORY_OPEN and SELECTED_CLOSED are mutually exclusive: open removes the badge,
        // close must restore it for completed categories so the checkmark/selection summary
        // remains visible when the panel is collapsed.
        if (
          category.classList.contains(
            'murphy-door-configurator__category-button--completed'
          )
        ) {
          DOM.addClass(
            category,
            'murphy-door-configurator__category-button--selected-closed'
          );
        }

        const thumbnails = DOM.getThumbnailsContainer(categoryId);
        if (thumbnails) {
          DOM.removeClass(
            thumbnails,
            'murphy-door-configurator__thumbnails--open'
          );
        }

        // Publish success event
        this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_COLLAPSED, {
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return true;
      } catch (error) {
        // Publish failure event with stack trace
        this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_COLLAPSE_FAILED, {
          error: error.message || error,
          stack: error.stack,
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
        isDev() &&
          console.error(
            `❌ NAVIGATION MANAGER: Category collapse failed - ${error.message}`,
            error.stack
          );
        return false;
      }
    },

    showCategoriesForTab(tabId) {
      const DOM = getDOM();
      if (!DOM) {
        isDev() &&
          console.warn(
            '🎯 NAVIGATION MANAGER: DOM not available, cannot show categories for tab:',
            tabId
          );
        return false;
      }

      // apo_style has no tabs -- every category is already visible via
      // buildCategories()'s reveal-all pass. This function's whole job is
      // "hide every category, then show only this tab's" (see below), which
      // would undo that reveal the moment ANYTHING calls it -- and
      // auto-navigation-coordinator publishes UI_TAB_OPEN_REQUEST (->
      // activateTab() -> here) unconditionally on every navigation trigger,
      // not just in tabbed modes. No-op here instead of fighting every
      // future caller to special-case apo_style individually.
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (conductor?._getConfiguratorExperience?.() === 'apo_style') {
        return true;
      }

      // 🔥 PPD-FIRST STRICT MODE: Enforce tab-to-category map dependency
      if (!(this._tabToCategoryElementsMap instanceof Map)) {
        throw new Error(
          'CRITICAL: _tabToCategoryElementsMap is not initialized. This indicates a failure in the buildCategories() method.'
        );
      }

      // Hide all categories first
      const allCategories = DOM.getElements(
        '.murphy-door-configurator__category-button'
      );
      if (allCategories) {
        Array.from(allCategories).forEach((cat) =>
          DOM.setElementDisplay(cat, false)
        );
      }

      // 🎯 SIMPLIFIED MODE: "My Door" virtual tab shows all simplified categories at once
      if (tabId === 'my-door' && simplifiedCategoryAllowlist !== null) {
        const simplifiedElements = allCategories
          ? Array.from(allCategories).filter((cat) =>
              simplifiedCategoryAllowlist.has(cat.getAttribute('data-category'))
            )
          : [];
        simplifiedElements.forEach((cat) => {
          if (cat.isConnected) {
            DOM.setElementDisplay(cat, true);
            if (cat.parentElement) cat.parentElement.appendChild(cat);
          }
        });
        this._hydrateVisiblePlaceholders(simplifiedElements);
        this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORIES_SHOWN_FOR_TAB, {
          tabId,
          categoryCount: simplifiedElements.length,
          timestamp: Date.now(),
          source: 'navigationManager.showCategoriesForTab',
        });
        this.publishTabReadinessState();
        return true;
      }

      // Show only categories for this tab (O(1) lookup + O(m) display where m = tab categories)
      const tabCategories = this._tabToCategoryElementsMap.get(tabId) || [];
      tabCategories.forEach((category) => {
        // ✅ SAFETY: Check isConnected to prevent errors with stale references
        if (category.isConnected) {
          // 🎯 SIMPLIFIED MODE: Skip categories not in the simplified allowlist
          if (simplifiedCategoryAllowlist !== null) {
            const catId = category.getAttribute('data-category');
            if (!simplifiedCategoryAllowlist.has(catId)) {
              return; // Leave hidden — simplified mode controls visibility
            }
          }
          DOM.setElementDisplay(category, true);
          // Re-append in correct order (maintains DOM sort order)
          if (category.parentElement) {
            category.parentElement.appendChild(category);
          }
        } else {
          isDev() &&
            console.warn(
              '🎯 NAVIGATION MANAGER: Stale category element reference found in map for tab',
              tabId
            );
        }
      });

      // 🚀 PERFORMANCE FIX (P0): On-demand placeholder hydration for visible tab only.
      // Replaces placeholders with real thumbnail HTML ONLY for the tab being shown.
      // This prevents 1,000+ images from entering the DOM at once (Murphy Bed 1.2 GB bug).
      this._hydrateVisiblePlaceholders(tabCategories);

      // CCP COMPLIANT: Publish categories shown event for coordination
      this.publishEvent(CONSTANTS.EVENTS.UI_CATEGORIES_SHOWN_FOR_TAB, {
        tabId,
        categoryCount: tabCategories.length,
        timestamp: Date.now(),
        source: 'navigationManager.showCategoriesForTab',
      });

      // CCP COMPLIANT: Publish tab readiness state after tab category display changes
      this.publishTabReadinessState();
      return true;
    },

    /**
     * PERFORMANCE FIX (P0): On-demand placeholder hydration.
     * Replaces loading placeholders with real thumbnail HTML ONLY for category
     * elements that are currently visible (i.e., belong to the active tab).
     *
     * This is the core fix for the Murphy Bed 1.2 GB image loading bug:
     * Previously, ALL categories had their thumbnails eagerly injected into the DOM
     * at build time, causing 1,000+ <img> tags to enter the DOM simultaneously.
     * Now, thumbnails are only injected when their parent tab becomes visible.
     *
     * @param {Array<HTMLElement>} categoryElements - Category elements to hydrate
     * @private
     */
    _hydrateVisiblePlaceholders(categoryElements) {
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (!conductor || !conductor.getVirtualizedProductsForCategory) {
        isDev() &&
          console.warn(
            'NAVIGATION MANAGER: Conductor not available for placeholder hydration'
          );
        return;
      }

      let hydratedCount = 0;

      categoryElements.forEach((categoryElement) => {
        if (!categoryElement.isConnected) return;

        const placeholder = categoryElement.querySelector(
          '.murphy-door-configurator__loading-placeholder'
        );
        if (!placeholder) return; // Already hydrated — no placeholder present

        const categoryId = placeholder.getAttribute('data-category');
        if (!categoryId) return;

        // Slider categories (Width, Height) render via renderSlider() in showCategoryContent().
        // Injecting product thumbnail HTML here causes them to show a product tile instead of
        // the drag slider when the category is auto-expanded.
        if (
          typeof conductor.shouldUseCategorySlider === 'function' &&
          conductor.shouldUseCategorySlider(categoryId)
        ) {
          isDev() &&
            console.log(
              'NAVIGATION MANAGER: Skipping placeholder hydration for slider category "' +
                categoryId +
                '"'
            );
          return;
        }

        const thumbnailsContainer = categoryElement.querySelector(
          '.murphy-door-configurator__thumbnails'
        );
        if (!thumbnailsContainer) return;

        try {
          const virtualizedProducts =
            conductor.getVirtualizedProductsForCategory(categoryId);

          if (virtualizedProducts && virtualizedProducts.length > 0) {
            const freshHTML = this.generateThumbnailsHTMLWithProducts(
              categoryId,
              virtualizedProducts
            );

            if (freshHTML) {
              // XSS-SAFE: PPD-generated HTML, sanitizeHTML wrapper for audit trail (Pattern #17)
              thumbnailsContainer.innerHTML = sanitizeHTML(freshHTML);
              hydratedCount++;

              // CCP COMPLIANT: Publish placeholder rerendered event for coordination
              this.publishEvent(CONSTANTS.EVENTS.UI_PLACEHOLDER_RERENDERED, {
                categoryId,
                productCount: virtualizedProducts.length,
                timestamp: Date.now(),
                source: 'navigationManager._hydrateVisiblePlaceholders',
              });
            }
          } else {
            isDev() &&
              console.warn(
                'NAVIGATION MANAGER: No virtualized products found for category "' +
                  categoryId +
                  '" during hydration'
              );
          }
        } catch (error) {
          isDev() &&
            console.error(
              'NAVIGATION MANAGER: Error hydrating placeholder for category "' +
                categoryId +
                '":',
              error
            );
        }
      });

      if (hydratedCount > 0) {
        isDev() &&
          console.log(
            'NAVIGATION MANAGER: On-demand hydrated ' +
              hydratedCount +
              ' category placeholders'
          );
      }
    },

    /* DOM Access Methods */
    getRequiredElements() {
      const DOM = getDOM();
      if (!DOM) {
        isDev() &&
          console.warn(
            '🎯 NAVIGATION MANAGER: DOM not available, cannot get required elements'
          );
        return {};
      }

      return {
        tabsContainer: DOM.getElement('.murphy-door-configurator__tabs'),
        summaryTab: DOM.getElement('[data-tab="summary"]'),
        svgTemplates: this.getSvgTemplates(),
      };
    },

    getSvgTemplates() {
      const DOM = getDOM();
      if (!DOM) {
        isDev() &&
          console.warn(
            '🎯 NAVIGATION MANAGER: DOM not available, cannot get SVG templates'
          );
        return {};
      }

      // ✅ FIX: Get the SVG element inside the hidden div, not the div itself
      // Remove fallback to ensure validation catches missing SVGs (fail fast, not silent)
      const openCircleContainer = DOM.getElement(
        '#murphy-door-open-circle-svg'
      );
      const circleCheckContainer = DOM.getElement(
        '#murphy-door-circle-check-svg'
      );
      const pencilEditContainer = DOM.getElement(
        '#murphy-door-pencil-edit-icon-svg'
      );
      const closeIconContainer = DOM.getElement('#murphy-door-close-icon-svg');
      const backArrowContainer = DOM.getElement('#murphy-door-back-arrow-svg');
      const infoIconContainer = DOM.getElement('#murphy-door-info-icon-svg');
      const measuringTapeContainer = DOM.getElement(
        '#murphy-door-measuring-tape-svg'
      );
      const saveIconContainer = DOM.getElement('#murphy-door-save-icon-svg');

      return {
        openCircle: openCircleContainer?.querySelector('svg'),
        circleCheck: circleCheckContainer?.querySelector('svg'),
        pencilEdit: pencilEditContainer?.querySelector('svg'),
        closeIcon: closeIconContainer?.querySelector('svg'),
        backArrow: backArrowContainer?.querySelector('svg'),
        infoIcon: infoIconContainer?.querySelector('svg'),
        measuringTape: measuringTapeContainer?.querySelector('svg'),
        saveIcon: saveIconContainer?.querySelector('svg'),
      };
    },

    /**
     * Validates that required DOM elements are available for navigation structure.
     *
     * @param {HTMLElement} tabsContainer - The tabs container element
     * @param {Object} svgTemplates - Object containing SVG templates
     * @returns {boolean} True if all elements are valid, false otherwise
     */
    validateElements(tabsContainer, svgTemplates) {
      if (
        !tabsContainer ||
        !svgTemplates ||
        !svgTemplates.openCircle ||
        !svgTemplates.circleCheck ||
        !svgTemplates.pencilEdit ||
        !svgTemplates.backArrow ||
        !svgTemplates.infoIcon
      ) {
        return false;
      }
      return true;
    },

    /**
     * Get fallback image URL for categories that don't have product images
     * Uses category-specific placeholder images to maintain visual consistency
     *
     * @param {string} categoryId - Category identifier
     * @returns {string|null} Fallback image URL or null if no fallback available
     */
    getFallbackImageForCategory(categoryId) {
      // 🔥 PPD-FIRST: Read from pre-built map via conductor delegation
      const conductor = window.MurphyDoorConfigurator.conductor;

      if (!conductor?.getCategoryFallbackImage) {
        throw new Error(
          'CRITICAL: Conductor getCategoryFallbackImage not available - PPD not initialized properly. This indicates a bootstrap failure.'
        );
      }

      const fallbackImage = conductor.getCategoryFallbackImage(categoryId);

      if (!fallbackImage) {
        throw new Error(
          `CRITICAL: No fallback image found for category "${categoryId}" in PPD categoryFallbackImages map. Check data pipeline Layer 2 image mapping.`
        );
      }

      return fallbackImage;
    },

    /**
     * 🔥 SURGICAL DRY REFACTOR: Shared thumbnail template with intentional UX differentiation
     *
     * This method preserves the critical UX differences between:
     * - LONELY_MULTI_VARIANT: Direct variant selection
     * - MULTI_VARIANT_ONLY: Product → variant flow
     * - SINGLE_VARIANT_ONLY: Direct product selection
     * - MIX: Smart mixed behavior
     *
     * Type configurations maintain proper CSS classes and data attributes
     * for different category behaviors and E2E testing support.
     *
     * @param {Object} config - Configuration object
     * @param {string} config.type - Thumbnail type: 'product' | 'composite' | 'variant'
     * @param {Object} config.data - Product/variant data object
     * @param {string} config.categoryId - Category identifier for fallback images
     * @param {number} config.index - Unique index for data attributes
     * @param {Object} [config.displayOverrides] - Optional display data overrides
     * @returns {string} Generated HTML for thumbnail with type-specific structure
     */
    _generateThumbnailHTML(config) {
      try {
        const { type, data, categoryId, index, displayOverrides = {} } = config;

        if (!data || !data.id) {
          const errorMsg = `THUMBNAIL ERROR: Invalid data provided (type: ${type})`;
          isDev() &&
            console.error(
              'NavigationManager._generateThumbnailHTML:',
              errorMsg
            );
          // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
          return `<div class="murphy-door-configurator__error-thumbnail">
            ${escapeHTML(errorMsg)}
          </div>`;
        }

        // 🎯 TYPE-SPECIFIC CONFIGURATIONS: Preserves intentional UX differences
        const typeConfigs = {
          product: {
            cssClasses: [
              'murphy-door-configurator__variant-item',
              'murphy-door-configurator__thumbnail-item',
            ],
            dataAttributes: (data, index) => ({
              'data-is-composite': 'false',
              'data-product-id': data.id,
              'data-is-single-variant': data.isSingleVariant || false,
              'data-index': index,
            }),
          },
          composite: {
            cssClasses: [
              'murphy-door-configurator__variant-item',
              'murphy-door-configurator__thumbnail-item',
              'murphy-door-configurator__thumbnail-item--composite',
            ],
            dataAttributes: (data, index) => ({
              'data-is-composite': 'true',
              'data-group-id': data['data-group-id']
                ? data['data-group-id']
                : '',
              'data-composite-group-name': data.display_name
                ? data.display_name
                : data.title,
              'data-display-name': data.display_name
                ? data.display_name
                : data.title,
              'data-product-count': data.variantCount || 0,
              'data-composite-id': data.id,
              'data-product-id': this._extractPrimaryProductId(data),
              'data-variant-id': this._extractPrimaryVariantId(data),
              'data-index': index,
            }),
          },
          variant: {
            // variant-item is REQUIRED, not cosmetic: the keydown funnel in
            // md-config-core-event-handler.js matches
            // '.murphy-door-configurator__variant-item[role="button"]', so
            // without it these tiles get a role and a tabindex but Enter/Space
            // still do nothing -- focusable and dead, which is worse than
            // unreachable. 'product' and 'composite' already carried it; only
            // this type was missing it. It also brings these tiles into the
            // .murphy-door-configurator__variant-item.forbidden-disabled
            // styling they were silently missing.
            cssClasses: [
              'murphy-door-configurator__variant-item',
              'murphy-door-configurator__thumbnail-item',
            ],
            dataAttributes: (data, index) => ({
              'data-variant-id': data.id,
              'data-product-id': data.sourceProductId
                ? data.sourceProductId
                : data.productId
                  ? data.productId
                  : this._resolveProductIdFromPPD(data.id),
              'data-index': index,
            }),
          },
        };

        const typeConfig = typeConfigs[type];
        if (!typeConfig) {
          const errorMsg = `TEMPLATE ERROR: Unknown thumbnail type "${type}"`;
          isDev() &&
            console.error(
              'NavigationManager._generateThumbnailHTML:',
              errorMsg
            );
          // 🔥 STRICT NO FALLBACK: Return visible error HTML for debugging
          return `<div class="murphy-door-configurator__error-thumbnail">
            ${escapeHTML(errorMsg)}
          </div>`;
        }

        // 🔥 STRICT NO FALLBACK: Extract image source without fallbacks
        const imageSource = this._extractImageSource(data, displayOverrides);
        const finalImageSrc = imageSource;

        // 🔥 PPD-FIRST DISPLAY TITLE: Get from perfectProductDictionary displayFormatting
        // ⚡ CONTEXT-AWARE: Use thumbnailDisplay for product thumbnails (product name only)
        const conductor = window.MurphyDoorConfigurator.conductor;
        const ppd = conductor?.getPerfectProductDictionary();
        let baseDisplay = data.display_name || '';
        let price =
          data.price || (data.variants && data.variants[0]?.price) || null;
        let variant; // ⚡ SCOPE FIX: Declare variant outside ppd block for Delivery check

        if (ppd) {
          const variantId =
            type === 'variant'
              ? data.id
              : data.primaryVariantId || data.variants?.[0]?.id;
          if (variantId) {
            variant = ppd.variantIdToVariantMap.get(variantId);
          }

          if (variant && variant.displayFormatting) {
            // ⚡ FIX: Use thumbnailDisplay for thumbnails (NOT baseDisplay which shows "Product | Variant")
            // thumbnailDisplay shows ONLY product name (no redundant variant name in thumbnail)
            baseDisplay =
              variant.displayFormatting.thumbnailDisplay ||
              variant.displayFormatting.baseDisplay;
          }
        }

        // Delivery category: Show shipping time instead of product name in thumbnail caption
        // Format: "{baseShippingTime} | Shipping Fee Calculated at Checkout" e.g., "1 - 3 Weeks | Shipping Fee Calculated at Checkout"
        // ONLY for FREE ($0.00) Delivery products (Standard Production)
        const isDeliveryProduct =
          variant?.displayFormatting?.isDeliveryCategory === true;
        const isFreeDelivery =
          isDeliveryProduct && parseFloat(price || 0) === 0;
        const isRushFeeProduct =
          variant?.displayFormatting?.isRushFeeProduct === true;

        let displayTitle = baseDisplay;
        let isRushDelivery = false; // Track whether this thumbnail gets the rush data attribute
        if (price) {
          const numericPrice =
            typeof price === 'string'
              ? parseFloat(price.replace(/,/g, ''))
              : parseFloat(price);
          if (!isNaN(numericPrice)) {
            // Whole dollars render as "$1,190", not "$1,190.00" — the shared
            // rule every price surface uses. Cents are NOT rounded away: the
            // Intl call still formats to 2 decimals and only a literal
            // trailing ".00" is stripped, so add-on prices like "$20.90"
            // survive intact. See utils.dropWholeDollarCents.
            // Guarded on purpose. This is a COSMETIC formatter inside the tile
            // builder, and the builder's catch replaces the whole tile with an
            // error thumbnail — so an unguarded call here lets a missing Phase 0
            // utility destroy an option tile over a trailing ".00". Degrade to
            // the unstripped price instead; that is a blemish, not an outage.
            const utils = window.MurphyDoorConfigurator.utils;
            const rawFormatted = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(numericPrice);
            const formattedPrice =
              utils && utils.dropWholeDollarCents
                ? utils.dropWholeDollarCents(rawFormatted)
                : rawFormatted;

            if (isFreeDelivery) {
              // FREE Delivery products ($0.00): Show shipping time | "Shipping Fee Calculated at Checkout"
              // The $0.00 price is misleading — actual shipping is calculated at Shopify checkout.
              // Render with PPD base production_time here. The uiUpdater module owns all
              // dynamic shipping time updates: patchDeliveryThumbnailCaptions() corrects
              // stale cached HTML before DOM injection, and updateShippingTime() patches
              // any already-rendered thumbnails live. Keeping this module decoupled from
              // live DOM state (owned by uiUpdater) maintains clean module separation.
              const freeDeliveryPriceSuffix =
                'Shipping Fee Calculated at Checkout';
              if (ppd && ppd.baseProduct && ppd.baseProduct.production_time) {
                const { low, high } = ppd.baseProduct.production_time;
                if (low !== null && high !== null) {
                  // En dash per Brand Style Guide 2024 p.26.
                  const shippingText = `${low} – ${high} Week${high > 1 ? 's' : ''}`;
                  displayTitle = `${shippingText} | ${freeDeliveryPriceSuffix}`;
                } else {
                  displayTitle = `${baseDisplay} | ${freeDeliveryPriceSuffix}`;
                }
              } else {
                displayTitle = `${baseDisplay} | ${freeDeliveryPriceSuffix}`;
              }
            } else if (isRushFeeProduct) {
              // RUSH FEE Delivery product: Show computed rush production time | price
              // Business logic is centralized in utils.computeRushShippingTime (DRY).
              // Rush time = base production_time reduced by 1 week on each bound.
              // Hide entirely if rushHigh < 1 (base was ~1 Week, rush not meaningful).
              if (ppd && ppd.baseProduct && ppd.baseProduct.production_time) {
                const { low, high } = ppd.baseProduct.production_time;
                if (low !== null && high !== null) {
                  const rushResult =
                    window.MurphyDoorConfigurator.utils.computeRushShippingTime(
                      { low, high }
                    );
                  if (rushResult.hidden) {
                    // Base production is ~1 Week - rush fee not applicable, HIDE thumbnail
                    // Returning '' causes .filter(html => html.length > 0) to exclude it
                    return '';
                  }
                  displayTitle = rushResult.text
                    ? `${rushResult.text} | ${formattedPrice} + Shipping Fee Calculated at Checkout`
                    : `${baseDisplay} | ${formattedPrice} + Shipping Fee Calculated at Checkout`;
                  isRushDelivery = true;
                } else {
                  // No production_time data - fallback to product name | price
                  displayTitle = `${baseDisplay} | ${formattedPrice} + Shipping Fee Calculated at Checkout`;
                  isRushDelivery = true;
                }
              } else {
                // No base product data - fallback to product name | price
                displayTitle = `${baseDisplay} | ${formattedPrice} + Shipping Fee Calculated at Checkout`;
                isRushDelivery = true;
              }
            } else if (isDeliveryProduct) {
              // Other PAID Delivery products: Show product name | price
              displayTitle = `${baseDisplay} | ${formattedPrice}`;
            } else {
              // A literal "$0" next to sibling tiles carrying real dollar
              // amounts reads as "is this broken?" -- Door Handing (in/out-
              // swing, left/right hand) has zero price variance across ANY of
              // its options, so every tile hit this. Matches the "(Included)"
              // convention formatConfiguratorPrice's zeroLabel already uses on
              // the Summary sidebar and category-button caption. Parenthesized,
              // not bare -- several live option names already start with "No"
              // (Knobs & Pulls, Cabinet Doors, Smart Home Automations,
              // Appearance Upgrades), and a bare "No Knobs or Pulls Included"
              // reads as a run-on sentence, not a price label.
              displayTitle =
                numericPrice === 0
                  ? `${baseDisplay} (Included)`
                  : `${baseDisplay} ${formattedPrice}`;
            }
          }
        }

        // 🔥 UNIFIED DATA ATTRIBUTES: Type-specific but consistent generation
        const dataAttrs = typeConfig.dataAttributes(data, index);
        const dataAttrString = Object.entries(dataAttrs)
          .map(([key, value]) => `${key}="${escapeHTML(String(value))}"`)
          .join(' ');

        // 🔥 STRICT NO FALLBACK HTML TEMPLATE: Fail if image missing
        if (!finalImageSrc) {
          throw new Error(
            `Thumbnail generation failed: Missing image source for ${type} with id ${data.id}`
          );
        }

        // Build delivery data attribute string for free and rush delivery thumbnails
        const deliveryAttr = isFreeDelivery
          ? ' data-is-free-delivery="true"'
          : isRushDelivery
            ? ' data-is-rush-delivery="true"'
            : '';

        // 🎯 THUMBNAIL STATUS ICON: Lazy-init module-level cache (O(1) after first call).
        // DOM queries only happen once per page load, not per thumbnail render.
        if (_cachedStatusIconHTML === null) {
          const _save =
            document
              .getElementById('murphy-door-save-icon-svg')
              ?.querySelector('svg')?.outerHTML || '';
          const _cc =
            document
              .getElementById('murphy-door-circle-check-svg')
              ?.querySelector('svg')?.outerHTML || '';
          if (_save && _cc) {
            // A <span>, not a <button>, and aria-hidden -- same call the gate
            // options already make (see generateGateCardHTML). Two reasons,
            // both load-bearing now that the tile itself is role="button":
            //   1. nested-interactive. A real <button> inside a role="button"
            //      is an axe violation and screen readers announce it as two
            //      overlapping controls.
            //   2. the label was a lie. .thumbnail-save-button has NO click
            //      handler anywhere in the codebase (its only other reference
            //      is an exclusion in the back-navigation branch of
            //      md-config-core-event-handler.js, which keys on the CLASS,
            //      so a span still matches and that behaviour is unchanged).
            //      Announcing "Save this configuration" on a dead control sent
            //      screen-reader users to a button that does nothing.
            // The CSS keys on the class, not the tag, and already sets
            // display/border/background/padding explicitly, so a span renders
            // identically (section-md-configurator.css ~2307).
            _cachedStatusIconHTML = `<div class="murphy-door-configurator__thumbnail-status-icon" aria-hidden="true">
              <span class="thumbnail-save-button">${_save}</span>
              <span class="thumbnail-circle-check">${_cc}</span>
            </div>`;
          } else {
            isDev() &&
              console.warn(
                '[NavigationManager] Thumbnail status SVG templates not found in DOM. ' +
                  'Ensure snippets icon-save and murphy-door-circle-check are included with correct IDs.'
              );
            _cachedStatusIconHTML = ''; // Mark unavailable - no retry needed (DOM is fully rendered by this point)
          }
        }
        const statusIconOverlay = _cachedStatusIconHTML;

        const _cdnUtil = window.MurphyDoorConfigurator?.utils?.cdnOptimizedUrl;
        const _baseW = 100,
          _maxW = 240;
        // Fallback mirrors getCdnThumbnailWidth logic — keep in sync if that utility changes.
        const _thumbW =
          window.MurphyDoorConfigurator?.utils?.getCdnThumbnailWidth?.(
            _baseW,
            _maxW
          ) ??
          Math.min(Math.ceil(_baseW * (window.devicePixelRatio || 1)), _maxW);
        const _finalThumbSrc = _cdnUtil
          ? _cdnUtil(finalImageSrc, _thumbW)
          : finalImageSrc;

        // P7 A11Y: option tiles are clickable <div>s; expose them as focusable
        // role="button" controls with a descriptive label so keyboard and
        // screen-reader users can select an option. Keyboard activation is the
        // delegated keydown funnel in md-config-core-event-handler.js, which
        // matches on '.murphy-door-configurator__variant-item[role="button"]'
        // -- both halves of that selector are required, which is why the
        // 'variant' type's cssClasses gained the variant-item class above.
        //
        // displayTitle already carries the price (see the block above), so it
        // is the same "title + price" label the PPD-cached tiles use in
        // md-config-core-data-pipeline.js's _generateVariantThumbnailHTML.
        // Escaped because door titles routinely contain `"` (e.g. 24"), which
        // would otherwise break out of the attribute.
        //
        // Attribute ORDER is deliberate: `class` stays the first attribute
        // after `<div `, which the material-filter regexes in
        // md-config-ui-updater.js:502 depend on, and role/tabindex/aria-label
        // go last, matching the shipped PPD template.
        const tileAriaLabel = escapeHTML(displayTitle);

        return `
          <div class="${typeConfig.cssClasses.join(' ')}" ${dataAttrString}${deliveryAttr} role="button" tabindex="0" aria-pressed="false" aria-label="${tileAriaLabel}">
            <div class="murphy-door-configurator__thumbnail-image">
              ${
                window.MurphyDoorConfigurator?.utils?.thumbnailImgHtml
                  ? window.MurphyDoorConfigurator.utils.thumbnailImgHtml({
                      src: _finalThumbSrc,
                      srcsetFrom: finalImageSrc,
                      alt: displayTitle,
                      selfClosing: true,
                    })
                  : `<img src="${escapeHTML(
                      _finalThumbSrc
                    )}" alt="${escapeHTML(
                      displayTitle
                    )}" width="100" height="100" loading="lazy" />`
              }
              ${statusIconOverlay}
            </div>
            <span class="murphy-door-configurator__thumbnail-caption">
              ${escapeHTML(displayTitle)}
            </span>
          </div>
        `;
      } catch (error) {
        isDev() &&
          console.error(
            'NavigationManager._generateThumbnailHTML error:',
            error
          );
        throw error; // 🔥 STRICT NO FALLBACK: Re-throw to fail loudly
      }
    },

    /**
     * 🔥 HELPER: Extract image source from various Shopify formats
     * 🎯 PHASE 2 CCP COMPLIANCE: Uses conductor delegation with strict error enforcement
     * STRICT MODE: Throws errors instead of graceful fallbacks to surface data integrity violations
     */
    _extractImageSource(data, overrides = {}) {
      if (overrides.imageSrc) return overrides.imageSrc;

      // 🔥 PPD-FIRST: Single O(1) map lookup via conductor delegation
      const conductor = window.MurphyDoorConfigurator?.conductor;

      if (!conductor?.getProductImageUrl) {
        throw new Error(
          'CRITICAL: Conductor getProductImageUrl not available - PPD not initialized. This indicates the Perfect Product Dictionary was not properly loaded before navigation rendering.'
        );
      }

      if (!data.id) {
        throw new Error(
          `CRITICAL: Product data missing required "id" property. Received data: ${JSON.stringify(data)}. This indicates malformed product data being passed to navigation manager - check data pipeline output.`
        );
      }

      const imageUrl = conductor.getProductImageUrl(data.id);

      if (!imageUrl) {
        throw new Error(
          `CRITICAL: No image URL found for product ID "${data.id}" in PPD productImageMap. This indicates a data integrity violation - the product exists but has no image mapping. Check data pipeline Layer 2 (image URL mapping).`
        );
      }

      return imageUrl;
    },

    /**
     * 🔥 HELPER: Detect single-variant products for MIX category behavior
     */
    _detectSingleVariant(product) {
      // 🔥 PPD-FIRST: Read pre-computed property from Layer 4 annotation
      // Anti-Pattern #4 Fix: Eliminates runtime variant counting
      if (product.isSingleVariant === undefined) {
        throw new Error(
          `Product "${product.id}" missing required isSingleVariant annotation from PPD. ` +
            'This indicates a data pipeline integrity violation.'
        );
      }
      return product.isSingleVariant;
    },

    /**
     * 🔥 HELPER: Extract primary product ID from CompositeProduct
     * 🚀 OPTIMIZATION: Now reads pre-computed property from PPD Layer 4 (eliminates runtime traversal)
     */
    _extractPrimaryProductId(compositeProduct) {
      // PPD Layer 4 pre-computes this during CompositeProduct creation
      if (compositeProduct.primaryProductId) {
        return compositeProduct.primaryProductId;
      }

      // Strict validation - if missing, PPD Layer 4 failed
      throw new Error(
        `CompositeProduct "${compositeProduct.id}" missing primaryProductId annotation from PPD Layer 4. This indicates a data pipeline integrity violation.`
      );
    },

    /**
     * 🔥 HELPER: Extract primary variant ID from CompositeProduct
     * 🚀 OPTIMIZATION: Now reads pre-computed property from PPD Layer 4 (eliminates runtime traversal)
     */
    _extractPrimaryVariantId(compositeProduct) {
      // PPD Layer 4 pre-computes this during CompositeProduct creation
      if (compositeProduct.primaryVariantId) {
        return compositeProduct.primaryVariantId;
      }

      // Strict validation - if missing, PPD Layer 4 failed
      throw new Error(
        `CompositeProduct "${compositeProduct.id}" missing primaryVariantId annotation from PPD Layer 4. This indicates a data pipeline integrity violation.`
      );
    },

    /**
     * 🔥 PPD-FIRST: Resolve product ID from Perfect Product Dictionary for variants (O(1) lookup)
     * ANTI-PATTERN FIX #1: Uses conductor delegation to variantIdToProductIdMap for O(1) performance
     * STRICT MODE: Throws errors instead of graceful fallbacks to surface data integrity violations
     */
    _resolveProductIdFromPPD(variantId) {
      // 🔥 PPD-FIRST: O(1) lookup via conductor delegation
      const conductor = window.MurphyDoorConfigurator.conductor;

      if (!conductor?.getProductIdForVariant) {
        throw new Error(
          'CRITICAL: Conductor getProductIdForVariant not available - PPD not initialized. This indicates the Perfect Product Dictionary was not properly loaded before navigation rendering.'
        );
      }

      const productId = conductor.getProductIdForVariant(variantId);

      if (!productId) {
        throw new Error(
          `CRITICAL: Variant ID "${variantId}" not found in variantIdToProductIdMap. This indicates a data integrity violation - the variant exists in the UI but is missing from the PPD variant mapping. Check data pipeline Layer 3 (variant enhancement).`
        );
      }

      return productId;
    },

    /**
     * CCP COMPLIANT: Publish tab readiness state for reactive synchronization
     * This enables the conductor to maintain cached state without polling
     * State Synchronization pattern - UI modules proactively publish state changes
     */
    publishTabReadinessState() {
      try {
        // Query current tab DOM state
        const tabElements = document.querySelectorAll('[data-tab]');
        // 🔥 CRITICAL FIX: More flexible active tab detection for mixed tab systems
        // First try specific murphy-tab-button selector, then fallback to any active tab
        let activeTab = document.querySelector(
          '.murphy-tab-button.active[data-tab]'
        );
        if (!activeTab) {
          // Fallback for dynamic tabs or other tab systems that use different classes
          activeTab = document.querySelector('[data-tab].active');
        }

        const tabReadinessState = {
          tabsExist: tabElements.length > 0,
          hasActiveTab: !!activeTab,
          tabCount: tabElements.length,
          source: 'navigationManager.publishTabReadinessState',
          timestamp: performance.now(),
        };

        isDev() &&
          console.log(
            '🎯 NAVIGATION MANAGER: Publishing tab readiness state',
            tabReadinessState
          );

        // CCP COMPLIANT: Proactively publish state changes via event bus
        this.publishEvent(
          CONSTANTS.EVENTS.UI_TAB_READINESS_CHANGED,
          tabReadinessState
        );
      } catch (error) {
        isDev() &&
          console.error(
            '❌ NAVIGATION MANAGER: Failed to publish tab readiness state',
            error
          );
      }
    },

    /**
     * PATTERN 2: publishEvent() Helper (10 points)
     * PATTERN 11: Async Event Publishing (10 points)
     * NOTE: Object.freeze() not needed - CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
     * @param {string} eventType - Event type constant from CONSTANTS.EVENTS.*
     * @param {Object} eventData - The event payload
     * @returns {Promise<boolean>} Success status
     */
    async publishEvent(eventType, eventData) {
      try {
        if (!CoreEventBus) {
          isDev() &&
            console.warn(
              `[NavigationManager] Event bus not available, cannot publish: ${eventType}`
            );
          return false;
        }
        const success = await CoreEventBus.publish(eventType, eventData);
        if (!success) {
          isDev() &&
            console.warn(`[NavigationManager] Failed to publish ${eventType}`);
        }
        return success;
      } catch (error) {
        isDev() &&
          console.error(
            `[NavigationManager] Error publishing ${eventType}:`,
            error
          );
        return false;
      }
    },

    /**
     * PATTERN 15: Cleanup all event subscriptions and reset module state.
     */
    cleanup() {
      isDev() &&
        console.log(
          `[NavigationManager] Cleaning up ${cleanupHandlers.length} subscriptions`
        );
      cleanupHandlers.forEach((unsubscribe) => {
        try {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        } catch (error) {
          isDev() &&
            console.error('[NavigationManager] Cleanup handler error:', error);
        }
      });
      cleanupHandlers.length = 0;
      bootstrapComplete = false;
      isInitialized = false;
      pendingCalls = [];
    },

    /**
     * Get current module bootstrap state for debugging.
     * PATTERN 18: Returns frozen state object.
     * @returns {Object} Current module state (frozen)
     */
    getBootstrapState() {
      return Object.freeze({
        isInitialized,
        bootstrapComplete,
        pendingCallCount: pendingCalls.length,
        cleanupHandlerCount: cleanupHandlers.length,
      });
    },

    getCategoryElementsForTab(tabId) {
      return this._tabToCategoryElementsMap?.get(tabId) || [];
    },
  };

  // =============================================================================
  // 🎯 STALE SELECTION TAB INDICATOR: Event Subscriptions
  // =============================================================================
  //
  // When a stale auto-applied selection is detected/cleared, add/remove a
  // `has-stale-selection` CSS class on the parent tab button for visual indication.
  if (CoreEventBus) {
    const unsubStaleDetected = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_STALE_SELECTION_DETECTED,
      (event) => {
        if (!bootstrapComplete) return; // Respect bootstrap barrier

        const categoryIds = event?.categoryIds;
        if (!Array.isArray(categoryIds) || categoryIds.length === 0) {
          isDev() &&
            console.warn(
              '[NavigationManager] UI_STALE_SELECTION_DETECTED event missing or empty categoryIds, skipping'
            );
          return;
        }

        isDev() &&
          console.log(
            `[NavigationManager] Stale selection detected for categories: ${categoryIds.join(', ')} - adding tab indicators`
          );

        const conductor = window.MurphyDoorConfigurator.conductor;
        const DOM = getDOM();
        if (!DOM || !conductor) return;

        categoryIds.forEach((categoryId) => {
          const parentTabId = conductor.getParentTabForCategory?.(categoryId);
          if (!parentTabId) return;

          const tabElement = DOM.getTabElement(parentTabId);
          if (
            tabElement &&
            !tabElement.classList.contains('has-stale-selection')
          ) {
            tabElement.classList.add('has-stale-selection');
            isDev() &&
              console.log(
                `[NavigationManager] Added has-stale-selection to tab "${parentTabId}" for category "${categoryId}"`
              );
          }
        });
      }
    );
    cleanupHandlers.push(unsubStaleDetected);

    const unsubStaleCleared = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_STALE_SELECTION_CLEARED,
      (event) => {
        if (!bootstrapComplete) return; // Respect bootstrap barrier

        const categoryIds = event?.categoryIds;
        if (!Array.isArray(categoryIds) || categoryIds.length === 0) {
          isDev() &&
            console.warn(
              '[NavigationManager] UI_STALE_SELECTION_CLEARED event missing or empty categoryIds, skipping'
            );
          return;
        }

        isDev() &&
          console.log(
            `[NavigationManager] Stale selection cleared for categories: ${categoryIds.join(', ')} - checking tab indicators`
          );

        const conductor = window.MurphyDoorConfigurator.conductor;
        const DOM = getDOM();
        if (!DOM || !conductor) return;

        // Group cleared categories by their parent tab
        const tabsToCheck = new Set();
        categoryIds.forEach((categoryId) => {
          const parentTabId = conductor.getParentTabForCategory?.(categoryId);
          if (parentTabId) {
            tabsToCheck.add(parentTabId);
          }
        });

        // For each affected tab, check if ANY remaining categories still have stale selections
        tabsToCheck.forEach((tabId) => {
          const tabElement = DOM.getTabElement(tabId);
          if (!tabElement) return;

          // Check if any category elements in this tab still have stale state
          const navManager = window.MurphyDoorConfigurator.navigationManager;
          const categoryElements = navManager.getCategoryElementsForTab(tabId);
          const hasRemainingStale = categoryElements.some((catEl) => {
            return catEl.querySelector('.is-stale') !== null;
          });

          if (!hasRemainingStale) {
            tabElement.classList.remove('has-stale-selection');
            isDev() &&
              console.log(
                `[NavigationManager] Removed has-stale-selection from tab "${tabId}" - no remaining stale categories`
              );
          }
        });
      }
    );
    cleanupHandlers.push(unsubStaleCleared);
  }

  // =============================================================================
  // 🎯 MATERIAL FILTERING INTEGRATION: Event Subscriptions
  // =============================================================================
  //
  // Subscribe to system:maps_initialized to set up material preference handling
  // PATTERN 15: Track ALL subscriptions in cleanupHandlers for proper resource management
  if (CoreEventBus) {
    const unsubMapsInit = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_MAPS_INITIALIZED,
      () => {
        isDev() &&
          console.log(
            '🎯 Navigation Manager: Subscribing to material preference events'
          );

        // Subscribe to material:preference_applied for cascading category updates
        const unsubMatPref = CoreEventBus.subscribe(
          CONSTANTS.EVENTS.MATERIAL_PREFERENCE_APPLIED,
          (eventData) => {
            const { materialType, materialSku, preferences } = eventData;
            const conductor = window.MurphyDoorConfigurator.conductor;
            const ppd = conductor?.getPerfectProductDictionary();

            if (!ppd || !ppd.categoryMaterialAwareness) {
              isDev() &&
                console.warn(
                  '⚠️ Navigation Manager: PPD or categoryMaterialAwareness not available'
                );
              return;
            }

            isDev() &&
              console.log(
                `🎯 Navigation Manager: Material preference applied - ${materialType}: ${materialSku}`
              );

            const categoryAwareness = ppd.categoryMaterialAwareness;

            // Iterate through all categories to find affected ones
            categoryAwareness.forEach((awareness, categoryId) => {
              const isAffected =
                (materialType === CONSTANTS.MATERIAL_TYPES.WOOD &&
                  awareness.affectedByWood) ||
                (materialType === CONSTANTS.MATERIAL_TYPES.FINISH &&
                  awareness.affectedByFinish);

              if (isAffected) {
                isDev() &&
                  console.log(
                    `🎯 Navigation Manager: Category ${categoryId} affected by ${materialType} change, updating...`
                  );

                const categoryProducts =
                  ppd.categoryToProductsMap.get(categoryId);

                if (categoryProducts && categoryProducts.length > 0) {
                  // 🎯 CCP COMPLIANT: Use conductor delegation instead of direct module call
                  const conductor = window.MurphyDoorConfigurator.conductor;
                  const sortedProducts =
                    conductor?.sortCategoryProductsByMaterial(
                      categoryId,
                      preferences
                    );

                  // 🎯 CCP GOLDEN STANDARD: Publish event for UI updater instead of direct call
                  // PATTERN 2: Use module-scope publishNavEvent (CONSTANTS.EVENTS.* compliance)
                  if (sortedProducts) {
                    // Publish event for category view and variant browser rerender
                    publishNavEvent(
                      CONSTANTS.EVENTS.UI_CATEGORY_AND_VARIANTS_NEED_RERENDER,
                      {
                        categoryId: categoryId,
                        sortedProducts: sortedProducts,
                        preferences: preferences,
                        timestamp: Date.now(),
                        source: 'ui-navigation-manager',
                      }
                    );
                  } else {
                    // If sorting fails, still update variant browsers
                    categoryProducts.forEach((product) => {
                      publishNavEvent(
                        CONSTANTS.EVENTS.UI_VARIANTS_VIEW_NEEDS_RERENDER,
                        {
                          productId: product.id,
                          categoryId: categoryId,
                          timestamp: Date.now(),
                          source: 'ui-navigation-manager',
                        }
                      );
                    });
                  }
                }
              }
            });

            isDev() &&
              console.log(
                '🎯 Navigation Manager: Material preference cascading updates complete'
              );
          }
        );
        cleanupHandlers.push(unsubMatPref);
      }
    );
    cleanupHandlers.push(unsubMapsInit);
  }
  // =============================================================================
})(window);
